/**
 * Single-instance premium quick view modal: loads product JSON and hydrates modular markup
 * (mirrors snippets/quick-view-*.liquid class names).
 * Qty/weight/cart: shared with plp-table-details.js via [data-details-*] contracts.
 */

const SEL = {
  root: '[data-quick-view-root]',
  dialog: '[data-quick-view-dialog]',
  mountImage: '[data-qvm-image]',
  mountInfo: '[data-qvm-info]',
  mountSizes: '[data-qvm-sizes]',
  mountCta: '[data-qvm-cta]',
  detailsPanel: '[data-quick-view-details-panel]',
  detailsRoot: '[data-quick-view-details]',
  close: '[data-quick-modal-close]',
  overlay: '.quick-modal__overlay',
};

/**
 * Shared weight cache between product-batch hydrate, quick-view, and details modal.
 * Populated by weight-service.js (products-batch). QV can request product-batch when needed.
 * @returns {{
 *   rawBySku: Record<string, unknown>,
 *   dataBySku: Record<string, { entries: Array<{ id: string, label: string, fields: Array<{ label: string, value: string }> }> }>,
 *   pendingBySku: Record<string, Promise<void>>,
 *   pendingBatchByKey: Record<string, Promise<void>>
 * }}
 */
function getSharedWeightStore() {
  const w = /** @type {any} */ (window);
  if (!w.__aoneWeightSharedStore) {
    w.__aoneWeightSharedStore = {
      rawBySku: Object.create(null),
      dataBySku: Object.create(null),
      pendingBySku: Object.create(null),
      pendingBatchByKey: Object.create(null),
    };
  }
  return w.__aoneWeightSharedStore;
}

/** @param {string} value */
function normalizeSku(value) {
  return String(value || '').trim();
}

/** @param {unknown} value */
function normalizeShopifyId(value) {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';
  if (stringValue.includes('/')) return stringValue.split('/').pop() || '';
  return stringValue;
}

/**
 * Wait for the in-flight products-batch request (if any).
 * @returns {Promise<void>}
 */
async function waitForCurrentWeightData() {
  const w = /** @type {any} */ (window);
  const api = w.AOneProductWeightsAPI || w.AoneBatchWeight;
  if (api && typeof api.waitForCurrentWeightData === 'function') {
    await api.waitForCurrentWeightData();
    return;
  }
  const active = w.AOneProductWeights?.activeRequest;
  if (active) await active;
}

/**
 * @param {object | null | undefined} product
 * @returns {string[]}
 */
function familyProductIds(product) {
  const api = /** @type {any} */ (window).AoneLinkedProducts;
  if (api && typeof api.collectProductIds === 'function') {
    const ids = api.collectProductIds(product);
    if (Array.isArray(ids) && ids.length) return ids.map((id) => String(id));
  }
  const id = normalizeShopifyId(product?.id);
  return id ? [id] : [];
}

/**
 * Copy every linked product's cached batch payload into the shared SKU store.
 * @param {object | null | undefined} product
 */
function syncFamilyWeightCaches(product) {
  const store = /** @type {any} */ (window).AOneProductWeights;
  if (!store?.products || typeof store.products.get !== 'function') return;
  familyProductIds(product).forEach((id) => {
    if (!store.products.has(id)) return;
    const payload = store.products.get(id);
    if (payload && typeof payload === 'object') syncProductDataToSharedStore(payload);
  });
}

/**
 * @param {string} handle
 * @param {AbortSignal} [signal]
 * @returns {Promise<object[]>}
 */
async function fetchLinkedProductPayload(handle, signal) {
  const empty = { products: [], mergedProductTitle: '' };
  const safe = String(handle || '').trim();
  if (!safe) return empty;
  const url = `/products/${encodeURIComponent(safe)}?view=linked-products`;
  const res = await fetch(url, {
    signal,
    headers: { Accept: 'application/json, text/plain, */*' },
  });
  if (!res.ok) return empty;
  const text = (await res.text()).trim();
  if (!text || (text[0] !== '[' && text[0] !== '{')) return empty;
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return { products: parsed, mergedProductTitle: '' };
  if (parsed && typeof parsed === 'object') {
    return {
      products: Array.isArray(parsed.products) ? parsed.products : [],
      mergedProductTitle:
        parsed.mergedProductTitle != null ? String(parsed.mergedProductTitle).trim() : '',
    };
  }
  return empty;
}

/**
 * @param {object} product
 * @param {string} handle
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>}
 */
async function attachLinkedFamily(product, handle, signal) {
  if (!product) return product;
  let payload = { products: [], mergedProductTitle: '' };
  try {
    payload = await fetchLinkedProductPayload(handle || product.handle, signal);
  } catch (err) {
    if (err && /** @type {Error} */ (err).name === 'AbortError') throw err;
    return product;
  }
  if (payload.mergedProductTitle) {
    product.merged_product_title = payload.mergedProductTitle;
  }
  const api = /** @type {any} */ (window).AoneLinkedProducts;
  if (!api || typeof api.mergeFamily !== 'function') return product;
  if (product.__linkedFamilyMerged === true) return product;
  if (!payload.products.length) return product;
  return api.mergeFamily(product, payload.products) || product;
}

/**
 * Sync a cached product-level WMS payload into the legacy per-SKU shared store.
 * products-batch may expose SKUs under `skus` or legacy `items`.
 * @param {Record<string, unknown>} productData
 */
function syncProductDataToSharedStore(productData) {
  const shared = getSharedWeightStore();
  const items = Array.isArray(productData?.skus)
    ? productData.skus
    : Array.isArray(productData?.items)
      ? productData.items
      : [];
  items.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const sku = normalizeSku(/** @type {Record<string, unknown>} */ (item).sku);
    if (!sku) return;
    shared.rawBySku[sku.toLowerCase()] = item;
    delete shared.dataBySku[sku.toLowerCase()];
  });
}

/**
 * Resolve product weight data from the in-memory products-batch cache.
 * @param {object} product
 * @returns {Record<string, unknown> | null}
 */
function getCachedProductWeightData(product) {
  const store = /** @type {any} */ (window).AOneProductWeights;
  if (!store?.products) return null;
  const productId = normalizeShopifyId(product?.id);
  if (productId && store.products.has(productId)) {
    return store.products.get(productId);
  }
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  for (const variant of variants) {
    const variantId = normalizeShopifyId(variant?.id);
    if (variantId && store.variants?.has(variantId)) {
      const entry = store.variants.get(variantId);
      if (entry?.productId && store.products.has(entry.productId)) {
        return store.products.get(entry.productId);
      }
    }
    const sku = normalizeSku(variant?.sku != null ? String(variant.sku) : '');
    if (sku && store.skus?.has(sku)) {
      const entry = store.skus.get(sku);
      if (entry?.productId && store.products.has(entry.productId)) {
        return store.products.get(entry.productId);
      }
      if (entry?.item) {
        const shared = getSharedWeightStore();
        shared.rawBySku[sku.toLowerCase()] = entry.item;
      }
    }
  }
  return null;
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toFiniteNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!match) return null;
    const n = Number(match[0]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * @param {unknown} node
 * @returns {number | null}
 */
function extractMinWeightFromNode(node) {
  if (node == null) return null;

  const direct = toFiniteNumber(node);
  if (direct != null && direct > 0) return direct;

  if (Array.isArray(node)) {
    let min = null;
    for (const item of node) {
      const n = extractMinWeightFromNode(item);
      if (n == null || n <= 0) continue;
      min = min == null ? n : Math.min(min, n);
    }
    return min;
  }

  if (typeof node !== 'object') return null;
  const obj = /** @type {Record<string, unknown>} */ (node);

  const preferredKeys = [
    'minWeight',
    'min_weight',
    'minimumWeight',
    'minimum_weight',
    'minWeightGrams',
    'min_weight_grams',
    'overallMinWeight',
    'overall_min_weight',
    'minimumWeightGrams',
    'minimum_weight_grams',
    'weight',
    'weight_g',
    'gross_weight',
    'net_weight',
  ];
  for (const key of preferredKeys) {
    if (!(key in obj)) continue;
    const n = extractMinWeightFromNode(obj[key]);
    if (n != null && n > 0) return n;
  }

  for (const [rawKey, rawValue] of Object.entries(obj)) {
    const key = String(rawKey || '')
      .replace(/[\s_-]+/g, '')
      .toLowerCase();
    if (!key.includes('weight')) continue;
    if (key.includes('minimum') || key.includes('min') || key === 'weight') {
      const n = toFiniteNumber(rawValue);
      if (n != null && n > 0) return n;
    }
  }

  for (const listKey of ['availableWeights', 'availableOptions', 'weights', 'items', 'results', 'data']) {
    if (!(listKey in obj)) continue;
    const fromList = extractMinWeightFromNode(obj[listKey]);
    if (fromList != null && fromList > 0) return fromList;
  }

  // WMS product-batch often nests min-weight under summary / variant payloads.
  for (const nestedKey of ['summary', 'variant', 'variants', 'item', 'payload', 'response']) {
    if (!(nestedKey in obj)) continue;
    const nested = extractMinWeightFromNode(obj[nestedKey]);
    if (nested != null && nested > 0) return nested;
  }

  return null;
}

/**
 * @param {unknown} node
 * @returns {number | null}
 */
function extractAvailableQtyFromNode(node) {
  if (!node || typeof node !== 'object') return null;
  const obj = /** @type {Record<string, unknown>} */ (node);

  const qtyKeys = [
    'count',
    'availableQty',
    'available_qty',
    'availableQuantity',
    'available_quantity',
    'availableStock',
    'available_stock',
    'totalAvailableQuantity',
    'total_available_quantity',
    'totalQty',
    'total_qty',
    'stock',
    'stock_qty',
    'inventoryQty',
    'inventory_qty',
    'inventory_quantity',
    'quantity',
    'qty',
  ];
  for (const key of qtyKeys) {
    if (!(key in obj)) continue;
    const qty = toFiniteNumber(obj[key]);
    if (qty != null && qty >= 0) return Math.floor(qty);
  }

  const optionsList = Array.isArray(obj.availableOptions)
    ? obj.availableOptions
    : Array.isArray(obj.availableWeights)
      ? obj.availableWeights
      : Array.isArray(obj.weights)
        ? obj.weights
        : null;
  if (optionsList) return optionsList.length;

  for (const listKey of ['items', 'results', 'data']) {
    if (!(listKey in obj)) continue;
    const bucket = obj[listKey];
    if (Array.isArray(bucket)) return bucket.length;
    if (bucket && typeof bucket === 'object') {
      const nested = extractAvailableQtyFromNode(bucket);
      if (nested != null) return nested;
    }
  }

  for (const nestedKey of ['summary', 'variant', 'variants', 'item', 'payload', 'response']) {
    if (!(nestedKey in obj)) continue;
    const nested = extractAvailableQtyFromNode(obj[nestedKey]);
    if (nested != null) return nested;
  }

  return null;
}

/**
 * Hydrate variant min-weight from already-fetched batch nodes.
 * Does NOT overwrite Shopify inventory_quantity — stock status stays Shopify-owned.
 * @param {object} product
 */
function applyBatchDataToProduct(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (!variants.length) return;
  const shared = getSharedWeightStore();
  const inventoryApi =
    /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
    const productId = normalizeShopifyId(product?.id);
  syncFamilyWeightCaches(product);

  variants.forEach((variant) => {
    if (!variant || typeof variant !== 'object') return;
    // Lock Shopify qty once so later WMS paths cannot overwrite stock display.
    if (
      variant.shopify_inventory_quantity == null &&
      Number.isFinite(Number(variant.inventory_quantity))
    ) {
      variant.shopify_inventory_quantity = Math.max(
        0,
        Math.floor(Number(variant.inventory_quantity))
      );
    }

    const sku = normalizeSku(variant.sku != null ? String(variant.sku) : '');
    if (!sku) return;

    const node = shared.rawBySku[sku.toLowerCase()];
    if (node === undefined) return;

    const minWeight = extractMinWeightFromNode(node);
    const currentWeight = toFiniteNumber(variant.weight);
    if ((currentWeight == null || currentWeight <= 0) && minWeight != null && minWeight > 0) {
      variant.weight = minWeight;
      variant.weight_unit = 'g';
    }

    if (
      inventoryApi &&
      productId &&
      typeof inventoryApi.getSkuAvailability === 'function' &&
      (typeof inventoryApi.isCartStateReady !== 'function' || inventoryApi.isCartStateReady())
    ) {
      const ownerId = normalizeShopifyId(variant.product_id || variant.productId || productId);
      const availability = inventoryApi.getSkuAvailability(ownerId, sku);
      if (availability && availability.status === 'loaded' && availability.loading !== true) {
        if (availability.minWeight != null && Number(availability.minWeight) > 0) {
          if (currentWeight == null || currentWeight <= 0) {
            variant.weight = Number(availability.minWeight);
            variant.weight_unit = 'g';
          }
        }
      }
    }
  });
}

/**
 * True when WMS batch data for this product is already in memory (no wait needed).
 * @param {object} product
 * @returns {boolean}
 */
function isWmsReadyForProduct(product) {
  const familyIds = familyProductIds(product);
  if (familyIds.length <= 1 && getCachedProductWeightData(product)) return true;
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  /** @type {string[]} */
  const skus = [];
  variants.forEach((variant) => {
    const sku = normalizeSku(variant?.sku != null ? String(variant.sku) : '');
    if (sku && !skus.includes(sku)) skus.push(sku);
  });
  if (!skus.length) return true;
  const shared = getSharedWeightStore();
  return skus.every((sku) => {
    const key = sku.toLowerCase();
    return shared.rawBySku[key] != null || shared.dataBySku[key] != null;
  });
}

/**
 * Hydrate quick-view weight data from products-batch cache and request product-batch when needed.
 * Joins the in-flight per-parent products-batch for THIS product only (via loadCurrentProductWeights).
 * Does not wait for sibling Collection/SLP parents — that left open Quick Views stuck on skeleton.
 * @param {object} product
 * @returns {Promise<{ ok: boolean, cached?: boolean, empty?: boolean, error?: unknown }>}
 */
async function prefetchWeightsForQuickView(product) {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  /** @type {string[]} */
  const skus = [];
  variants.forEach((variant) => {
    const sku = normalizeSku(variant && variant.sku != null ? String(variant.sku) : '');
    if (sku && !skus.includes(sku)) skus.push(sku);
  });
  if (!skus.length) return { ok: true, empty: true };

  const shared = getSharedWeightStore();
  const alreadyCached = skus.every((sku) => {
    const key = sku.toLowerCase();
    return shared.rawBySku[key] != null || shared.dataBySku[key] != null;
  });
  if (alreadyCached) return { ok: true, cached: true };

  const familyIds = familyProductIds(product);
  const cachedProductData = getCachedProductWeightData(product);
  if (familyIds.length <= 1 && cachedProductData) {
    syncProductDataToSharedStore(cachedProductData);
    return { ok: true, cached: true };
  }

  const productId = familyIds[0] || normalizeShopifyId(product?.id);
  const api = /** @type {any} */ (window).AOneProductWeightsAPI || /** @type {any} */ (window).AoneBatchWeight;
  if (api && typeof api.loadCurrentProductWeights === 'function' && productId) {
    try {
      // Reuses inFlightByProductId when Collection/SLP already started this parent.
      await api.loadCurrentProductWeights(familyIds.length ? familyIds : [productId]);
      syncFamilyWeightCaches(product);
      const refreshedProductData = getCachedProductWeightData(product);
      if (refreshedProductData || isWmsReadyForProduct(product)) {
        return { ok: true };
      }
    } catch (error) {
      return { ok: false, error };
    }
  } else {
    // Legacy fallback when weight-service API is absent: wait for any active page request.
    await waitForCurrentWeightData();
    const afterWait = getCachedProductWeightData(product);
    if (afterWait) {
      syncProductDataToSharedStore(afterWait);
      return { ok: true };
    }
  }

  // Last resort: pull any SKU entries already indexed by weight-service
  const store = /** @type {any} */ (window).AOneProductWeights;
  if (store?.skus) {
    skus.forEach((sku) => {
      const entry = store.skus.get(sku) || store.skus.get(sku.toUpperCase());
      if (entry?.item) {
        shared.rawBySku[sku.toLowerCase()] = entry.item;
        delete shared.dataBySku[sku.toLowerCase()];
      }
    });
  }

  const anySkuCached = skus.some((sku) => {
    const key = sku.toLowerCase();
    return shared.rawBySku[key] != null || shared.dataBySku[key] != null;
  });
  if (anySkuCached) return { ok: true };

  if (store?.status === 'error') {
    return { ok: false, error: store.lastError || new Error('WMS request failed') };
  }

  return { ok: true, empty: true };
}

/** @param {string} s */
function escapeAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * @param {ParentNode | null | undefined} scope
 * @returns {Record<string, any> | null}
 */
function parseProductJsonFromScope(scope) {
  if (!scope || typeof scope.querySelector !== 'function') return null;
  const script = scope.querySelector('[data-qvm-product-json], [data-plp-product-json]');
  if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;
  try {
    const parsed = JSON.parse(script.textContent);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {any} variant
 * @returns {Record<string, any>}
 */
function normalizeVariantPayload(variant) {
  const safe = variant && typeof variant === 'object' ? variant : {};
  const normalized = { ...safe };
  normalized.id = safe.id != null ? safe.id : '';
  normalized.sku = safe.sku != null ? String(safe.sku) : '';
  normalized.available = safe.available !== false;
  const qty =
    typeof safe.inventory_quantity === 'number' && Number.isFinite(safe.inventory_quantity)
      ? Math.floor(safe.inventory_quantity)
      : safe.inventory_quantity;
  normalized.inventory_quantity = qty;
  const preservedShopify = Number(safe.shopify_inventory_quantity);
  if (Number.isFinite(preservedShopify) && preservedShopify >= 0) {
    normalized.shopify_inventory_quantity = Math.floor(preservedShopify);
  } else if (typeof qty === 'number' && Number.isFinite(qty) && qty >= 0) {
    normalized.shopify_inventory_quantity = Math.floor(qty);
  }
  return normalized;
}

/**
 * @param {any} product
 * @param {string} [fallbackHandle]
 * @returns {Record<string, any> | null}
 */
function normalizeProductPayload(product, fallbackHandle = '') {
  if (!product || typeof product !== 'object') return null;
  const safe = { ...product };
  const variants = Array.isArray(product.variants) ? product.variants.map(normalizeVariantPayload) : [];
  const normalizedHandle =
    String(safe.handle || '').trim() ||
    String(safe.url || '')
      .trim()
      .match(/\/products\/([^/?#]+)/)?.[1] ||
    String(fallbackHandle || '').trim();

  safe.id = safe.id != null ? safe.id : '';
  safe.handle = normalizedHandle;
  safe.url =
    String(safe.url || '').trim() ||
    (normalizedHandle ? `/products/${encodeURIComponent(normalizedHandle)}` : '');
  safe.title = String(safe.title || '').trim() || 'Product';
  safe.vendor = String(safe.vendor || '').trim();
  safe.product_type = String(safe.product_type || safe.type || '').trim();
  safe.type = String(safe.type || safe.product_type || '').trim();
  safe.available = safe.available !== false;
  safe.options = Array.isArray(safe.options) ? safe.options : [];
  safe.images = Array.isArray(safe.images) ? safe.images : [];
  safe.variants = variants;
  if (!safe.featured_image && safe.image) safe.featured_image = safe.image;
  if (!safe.image && safe.featured_image) safe.image = safe.featured_image;
  return safe;
}

/**
 * @param {Element | null} sourceEl
 * @param {string} fallbackHandle
 * @returns {Record<string, any> | null}
 */
function resolveCallerProductPayload(sourceEl, fallbackHandle) {
  if (!(sourceEl instanceof Element)) return null;
  const scopes = [
    sourceEl.closest('[data-plp-expanded-details]'),
    sourceEl.closest('.inventory-product-item'),
    sourceEl.closest('.plp-product-grid__item'),
    sourceEl.closest('.plp-card'),
    sourceEl.closest('.inventory-product-row'),
    sourceEl.closest('.image-search__product-card'),
  ];
  for (const scope of scopes) {
    const payload = normalizeProductPayload(parseProductJsonFromScope(scope), fallbackHandle);
    if (payload) return payload;
  }
  return null;
}

/**
 * @param {object} product
 * @returns {string[]}
 */
function getProductImageUrls(product) {
  const urls = [];
  const push = (u) => {
    if (!u) return;
    const s = String(u);
    if (s && urls.indexOf(s) === -1) urls.push(s);
  };
  if (Array.isArray(product.images)) {
    product.images.forEach((img) => {
      if (typeof img === 'string') push(img);
      else if (img && typeof img === 'object') push(img.src || img.url);
    });
  }
  push(product.featured_image);
  push(product.image);
  return urls;
}

/**
 * @param {HTMLElement | null | undefined} root
 * @returns {string}
 */
function getPlaceholderProductImage(root) {
  const fromRoot = root instanceof HTMLElement ? root.getAttribute('data-placeholder-product-image') : '';
  if (fromRoot) return String(fromRoot);
  return '/assets/placeholder-product.svg';
}

/**
 * @param {object} product
 * @param {string} placeholderImage
 * @param {{ isChain?: boolean }} [options]
 * @returns {string}
 */
function buildImageHtml(product, placeholderImage, options = {}) {
  const urls = getProductImageUrls(product);
  const alt = escapeAttr(product.title || '');
  const placeholder = escapeAttr(String(placeholderImage || ''));
  const isChain = options.isChain === true;
  const imageErrorFallback = placeholder
    ? ` onerror="this.onerror=null;this.removeAttribute('srcset');this.src='${placeholder}';"`
    : '';
  if (!urls.length) {
    // Merged Chain Quick View: omit media entirely when no valid product images exist.
    // Non-chain keeps the compact placeholder thumbnail layout.
    if (isChain) return '';
    return `<div class="quick-view-image" data-qvm-gallery data-placeholder-product-image="${placeholder}"><div class="quick-view-image__stage"><img class="quick-view-image__img quick-view-image__placeholder" data-qvm-main-image src="${placeholder}" alt="${alt}" loading="lazy" width="120" height="120" /></div></div>`;
  }
  const main = urls[0];
  const imageData = urls
    .map(
      (url, index) =>
        `<span data-qvm-popup-image data-image-src="${escapeAttr(url)}" data-image-alt="${alt}"${
          index === 0 ? ' data-active-image' : ''
        } hidden></span>`
    )
    .join('');

  const thumbs = `<div class="quick-view-image__thumbs${isChain ? ' quick-view-image__thumbs--horizontal' : ''}" role="list" aria-label="Product images"${
    isChain ? '' : ' hidden'
  }>
      ${urls
        .map(
          (url, index) => `
        <button
          type="button"
          class="quick-view-image__thumb${index === 0 ? ' is-active' : ''}"
          role="listitem"
          data-qvm-thumb
          data-qvm-thumb-src="${escapeAttr(url)}"
          aria-label="View image ${index + 1}"
          aria-pressed="${index === 0 ? 'true' : 'false'}"
        >
          <img
            class="quick-view-image__thumb-img"
            src="${escapeAttr(url)}"
            alt="${alt}"
            loading="lazy"
            width="80"
            height="80"${imageErrorFallback}
          >
          ${
            isChain
              ? `<span class="quick-view-image__thumb-zoom" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
              <circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.7"></circle>
              <path d="M16 16l4 4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
            </svg>
          </span>`
              : ''
          }
        </button>`
        )
        .join('')}
    </div>`;

  if (isChain) {
    return `
<div class="quick-view-image quick-view-image--horizontal" data-qvm-gallery data-placeholder-product-image="${placeholder}" data-gallery-src="${escapeAttr(String(main || ''))}">
  <div class="quick-view-image__stage quick-view-image__stage--sr">
    <img class="quick-view-image__img" data-qvm-main-image src="${escapeAttr(String(main || ''))}" alt="${alt}" loading="lazy" width="120" height="120"${imageErrorFallback} />
  </div>
  ${thumbs}
  <button type="button" class="quick-view-image__view-button quick-view-image__view-button--sr" data-qvm-view-images aria-label="View product images">
    <span>View Images</span>
  </button>
  <span class="quick-view-image__popup-data" aria-hidden="true">${imageData}</span>
</div>`;
  }

  return `
<div class="quick-view-image" data-qvm-gallery data-placeholder-product-image="${placeholder}" data-gallery-src="${escapeAttr(String(main || ''))}">
  <div class="quick-view-image__stage">
    <img class="quick-view-image__img" data-qvm-main-image src="${escapeAttr(String(main || ''))}" alt="${alt}" loading="lazy" width="120" height="120"${imageErrorFallback} />
  </div>
  <button type="button" class="quick-view-image__view-button" data-qvm-view-images aria-label="View product images">
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.7"/>
      <circle cx="9" cy="10" r="2" stroke="currentColor" stroke-width="1.7"/>
      <path d="m5 18 5-5 3 3 2-2 4 4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <span>View Images</span>
  </button>
  <span class="quick-view-image__popup-data" aria-hidden="true">${imageData}</span>
  ${thumbs}
</div>`;
}

/**
 * Shopify /products/{handle}.js may return options as strings or { name } objects.
 * @param {object} product
 * @returns {string[]}
 */
function getOptionNames(product) {
  const opts = product.options || [];
  return opts.map((opt) => {
    if (opt == null || opt === '') return 'Option';
    if (typeof opt === 'string') return opt;
    if (typeof opt === 'object' && opt !== null && 'name' in opt && opt.name != null) {
      return String(opt.name);
    }
    return String(opt);
  });
}

/**
 * @param {object} product
 * @returns {string}
 */
function getFirstOptionLabel(product) {
  const names = getOptionNames(product);
  return names[0] || 'Size';
}

/**
 * @param {string} value
 */
function normalizeKey(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * Material / karat option names must never be used as the WIDTH (MM) axis.
 * @param {string} key
 */
function isMaterialAxisKey(key) {
  const normalized = normalizeKey(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return /(material|karat|carat|metal|purity|jewellery|jewelry)/.test(normalized);
}

/**
 * @param {string} key
 */
function isWidthAxisKey(key) {
  const normalized = normalizeKey(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (isMaterialAxisKey(normalized)) return false;
  return /(width|thickness|gauge|\bmm\b|diameter|\bdia\b)/.test(normalized);
}

/**
 * @param {string} key
 */
function isLengthAxisKey(key) {
  const normalized = normalizeKey(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return /(length|inch|inches|\blen\b)/.test(normalized);
}

/**
 * @param {unknown} value
 */
function isKaratLikeValue(value) {
  return /^\d{1,2}\s*k(?:t)?$/i.test(String(value == null ? '' : value).trim());
}

/**
 * Display width as the numeric mm value (10mm → 10).
 * @param {unknown} value
 */
function normalizeWidthLabel(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw || raw === '—') return raw || '—';
  if (isKaratLikeValue(raw)) return raw;
  const mmMatch = raw.match(/^(\d+(?:\.\d+)?)\s*mm$/i);
  if (mmMatch) return mmMatch[1];
  return raw;
}

/**
 * Read fixed product width from title/handle/tags when Width is not a variant option.
 * Example: "14KT GOLD CHAIN ... 10MM" → "10"
 * @param {object} product
 * @returns {string}
 */
function extractWidthFromProduct(product) {
  if (!product || typeof product !== 'object') return '';
  const texts = [];
  if (product.title) texts.push(String(product.title));
  if (product.handle) texts.push(String(product.handle).replace(/[-_]+/g, ' '));
  if (Array.isArray(product.tags)) {
    product.tags.forEach(function (tag) {
      if (tag != null && String(tag).trim()) texts.push(String(tag));
    });
  }
  for (let i = 0; i < texts.length; i += 1) {
    const match = texts[i].match(/(\d+(?:\.\d+)?)\s*mm\b/i);
    if (match) return normalizeWidthLabel(match[1]);
  }
  return '';
}

/**
 * @param {object} product
 * @param {number} idx
 * @returns {string[]}
 */
function getOptionSampleValues(product, idx) {
  const variants = Array.isArray(product && product.variants) ? product.variants : [];
  const values = [];
  const seen = Object.create(null);
  for (let i = 0; i < variants.length; i += 1) {
    const value = getVariantOptionValue(variants[i], idx);
    if (!value || seen[value]) continue;
    seen[value] = true;
    values.push(value);
    if (values.length >= 8) break;
  }
  return values;
}

/**
 * @param {object} product
 * @returns {boolean}
 */
function isChainProduct(product) {
  const type = normalizeKey(product.product_type || product.type);
  if (type === 'chain' || type === 'chains') return true;

  if (Array.isArray(product.tags)) {
    const hasChainTag = product.tags.some((tag) => /\bchain(s)?\b/i.test(String(tag)));
    if (hasChainTag) return true;
  }

  if (Array.isArray(product.collections)) {
    const hasChainCollection = product.collections.some((col) => {
      const title = normalizeKey(col && (col.title || col.handle || col.name));
      return title.includes('chain');
    });
    if (hasChainCollection) return true;
  }

  return false;
}

/**
 * @param {object} product
 * @returns {boolean}
 */
function isBraceletProduct(product) {
  const type = normalizeKey(product.product_type || product.type);
  if (type === 'bracelet' || type === 'bracelets') return true;

  if (Array.isArray(product.tags)) {
    const hasBraceletTag = product.tags.some((tag) => /\bbracelet(s)?\b/i.test(String(tag)));
    if (hasBraceletTag) return true;
  }

  if (Array.isArray(product.collections)) {
    const hasBraceletCollection = product.collections.some((col) => {
      const title = normalizeKey(col && (col.title || col.handle || col.name));
      return title.includes('bracelet');
    });
    if (hasBraceletCollection) return true;
  }

  return false;
}

/**
 * Products that use the Width × Length bulk ordering matrix (chains + bracelets).
 * @param {object} product
 * @returns {boolean}
 */
function isMatrixProduct(product) {
  return isChainProduct(product) || isBraceletProduct(product);
}

/**
 * @param {string[]} values
 * @returns {string[]}
 */
function sortMatrixValues(values) {
  return values.slice().sort((a, b) => {
    const aNum = parseFloat(String(a).replace(/[^0-9.]+/g, ''));
    const bNum = parseFloat(String(b).replace(/[^0-9.]+/g, ''));
    const aHasNum = Number.isFinite(aNum);
    const bHasNum = Number.isFinite(bNum);
    if (aHasNum && bHasNum && aNum !== bNum) return aNum - bNum;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  });
}

/**
 * @param {object} variant
 * @param {number} idx
 * @returns {string}
 */
function getVariantOptionValue(variant, idx) {
  const key = /** @type {'option1'|'option2'|'option3'} */ (`option${idx + 1}`);
  const direct = variant[key];
  if (direct != null && String(direct).trim()) return String(direct);
  if (Array.isArray(variant.options) && variant.options[idx] != null) return String(variant.options[idx]);
  return '';
}

/**
 * @param {object | null | undefined} maybeVariant
 * @returns {boolean}
 */
/**
 * Keep first mapped variant stable.
 * Do not prefer Shopify variant.available — WMS owns cell availability by SKU.
 * @param {object | undefined} current
 * @param {object} next
 * @returns {object}
 */
function chooseMatrixVariant(current, next) {
  if (!current) return next;
  return current;
}

/**
 * @param {object} product
 * @returns {{ widthIdx: number, lengthIdx: number, fixedWidth: string }}
 */
function resolveChainAxes(product) {
  const optionNames = getOptionNames(product);
  let widthIdx = -1;
  let lengthIdx = -1;

  optionNames.forEach((name, idx) => {
    const key = normalizeKey(name);
    if (widthIdx < 0 && isWidthAxisKey(key)) widthIdx = idx;
    if (lengthIdx < 0 && isLengthAxisKey(key)) lengthIdx = idx;
  });

  // Never fall back to material/karat (e.g. "14KT") as the WIDTH axis.
  if (widthIdx < 0) {
    for (let idx = 0; idx < optionNames.length; idx += 1) {
      if (idx === lengthIdx) continue;
      if (isMaterialAxisKey(normalizeKey(optionNames[idx]))) continue;
      const samples = getOptionSampleValues(product, idx);
      if (!samples.length) continue;
      if (samples.every(isKaratLikeValue)) continue;
      const widthLikeCount = samples.filter(function (value) {
        const raw = String(value || '').trim();
        return /^\d+(?:\.\d+)?(?:\s*mm)?$/i.test(raw) && !isKaratLikeValue(raw);
      }).length;
      if (widthLikeCount > 0) {
        widthIdx = idx;
        break;
      }
    }
  }

  if (lengthIdx < 0) {
    for (let idx = 0; idx < optionNames.length; idx += 1) {
      if (idx === widthIdx) continue;
      if (isMaterialAxisKey(normalizeKey(optionNames[idx]))) continue;
      lengthIdx = idx;
      break;
    }
  }

  if (lengthIdx < 0) {
    if (optionNames.length > 1) lengthIdx = widthIdx === 0 ? 1 : 0;
    else if (optionNames.length === 1 && widthIdx !== 0) lengthIdx = 0;
    else lengthIdx = widthIdx;
  }
  if (lengthIdx === widthIdx && optionNames.length > 1) {
    lengthIdx = widthIdx === 0 ? 1 : 0;
  }

  const fixedWidth = widthIdx < 0 ? extractWidthFromProduct(product) : '';
  return { widthIdx, lengthIdx, fixedWidth };
}

/**
 * Variant title from /products/{handle}.js — sometimes missing or non-string; avoid "[object Object]".
 * @param {object} variant
 * @returns {string}
 */
function variantDisplayTitle(variant) {
  if (!variant || typeof variant !== 'object') return '';
  const t = variant.title;
  if (typeof t === 'string' && t.trim()) return t;
  const n = variant.name;
  if (typeof n === 'string' && n.trim()) return n;
  const parts = [variant.option1, variant.option2, variant.option3].filter(
    (x) => x != null && String(x).trim() !== ''
  );
  if (parts.length) return parts.map((x) => String(x)).join(' / ');
  if (t != null && typeof t === 'object' && 'name' in t && t.name != null) return String(t.name);
  return '';
}

/**
 * Same decision as the final Quick View renderer (`isMatrixProduct` → matrix, otherwise the variants table).
 * Returns "unknown" when product type / tags / collections are not available yet.
 * @param {object | null | undefined} product
 * @returns {'matrix' | 'table' | 'unknown'}
 */
function resolveQuickViewSkeletonLayout(product) {
  if (!product || typeof product !== 'object') return 'unknown';
  const type = String(product.product_type || product.type || '').trim();
  const hasTags = Array.isArray(product.tags) && product.tags.some((tag) => String(tag || '').trim());
  const hasCollections = Array.isArray(product.collections) && product.collections.length > 0;
  if (!type && !hasTags && !hasCollections) return 'unknown';
  return isMatrixProduct(product) ? 'matrix' : 'table';
}

/**
 * True when type, tags, or collections can already answer `isMatrixProduct`.
 * @param {object | null | undefined} product
 * @returns {boolean}
 */
function productHasLayoutSignal(product) {
  if (!product || typeof product !== 'object') return false;
  const type = String(product.product_type || product.type || '').trim();
  const hasTags = Array.isArray(product.tags) && product.tags.some((tag) => String(tag || '').trim());
  const hasCollections = Array.isArray(product.collections) && product.collections.length > 0;
  return Boolean(type || hasTags || hasCollections);
}

/**
 * Collection handle from the current page.
 * `isChainProduct` / `isBraceletProduct` already treat a collection title or handle as a matrix signal.
 * Grid cards often have a handle and no product type, so this is the same signal the loaded view can use.
 * @returns {{ handle: string, title: string } | null}
 */
function layoutCollectionFromLocation() {
  if (typeof window === 'undefined') return null;
  const path = String(window.location.pathname || '').toLowerCase();
  const match = path.match(/\/collections\/([^/?#]+)/);
  if (!match || !match[1]) return null;
  let handle = match[1];
  try {
    handle = decodeURIComponent(handle);
  } catch (_error) {
    handle = match[1];
  }
  handle = handle.trim();
  if (!handle) return null;
  return { handle, title: handle };
}

/**
 * Card attribute used when Quick View is opened with a handle only.
 * Fed into `isMatrixProduct` — not a second layout heuristic.
 * @param {string} handle
 * @returns {object | null}
 */
function lookupLayoutProductByHandle(handle) {
  const safe = String(handle || '').trim();
  let type = '';
  if (safe && typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    try {
      const nodes = document.querySelectorAll(`[data-product-handle="${CSS.escape(safe)}"]`);
      for (let i = 0; i < nodes.length; i += 1) {
        const el = nodes[i];
        if (!(el instanceof Element)) continue;
        const ownType = String(el.getAttribute('data-product-type') || '').trim();
        if (ownType) {
          type = ownType;
          break;
        }
        const host = el.closest('[data-product-type]');
        const hostType = host instanceof Element ? String(host.getAttribute('data-product-type') || '').trim() : '';
        if (hostType) {
          type = hostType;
          break;
        }
      }
    } catch (_error) {
      type = '';
    }
  }
  if (type) return { product_type: type, type };
  const collection = layoutCollectionFromLocation();
  if (collection) return { collections: [collection] };
  return null;
}

/**
 * Compact placeholder for a normal variants table (not the Width × Length matrix).
 * @param {'table' | 'unknown'} kind
 * @returns {{ image: string, info: string, sizes: string, cta: string }}
 */
function buildNonMatrixQuickViewLoadingSkeleton(kind) {
  const info = `
<div class="quick-view-product-info quick-view-loading-skeleton" data-qvm-loading-skeleton>
  <button type="button" class="quick-view-product-info__close" data-quick-modal-close aria-label="Close">
    <span aria-hidden="true">×</span>
  </button>
  <h2 class="visually-hidden" id="QuickViewModalTitle">Loading product details</h2>
  <span class="quick-view-skeleton quick-view-skeleton--collection" aria-hidden="true"></span>
  <span class="quick-view-skeleton quick-view-skeleton--title" aria-hidden="true"></span>
  <span class="quick-view-skeleton quick-view-skeleton--title quick-view-skeleton--title-line2" aria-hidden="true"></span>
  <div class="quick-view-product-info__badges" aria-hidden="true">
    <span class="quick-view-skeleton quick-view-skeleton--badge"></span>
    <span class="quick-view-skeleton quick-view-skeleton--badge quick-view-skeleton--badge-wide"></span>
  </div>
</div>`;
  const image = `
<div class="quick-view-image quick-view-loading-skeleton" data-qvm-loading-skeleton aria-hidden="true">
  <div class="quick-view-image__stage">
    <span class="quick-view-skeleton quick-view-skeleton--media" aria-hidden="true"></span>
  </div>
</div>`;
  const cta = `
<div class="quick-view-add-to-cart quick-view-loading-skeleton" data-qvm-loading-skeleton data-quick-view-cta aria-hidden="true">
  <div class="quick-view-add-to-cart__summary">
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Selected Variants:</span>
      <span class="quick-view-skeleton quick-view-skeleton--stat-value"></span>
    </div>
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Total Quantity:</span>
      <span class="quick-view-skeleton quick-view-skeleton--stat-value"></span>
    </div>
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Total Weight:</span>
      <span class="quick-view-skeleton quick-view-skeleton--stat-value"></span>
    </div>
  </div>
  <div class="quick-view-add-to-cart__actions">
    <span class="quick-view-skeleton quick-view-skeleton--cta"></span>
  </div>
</div>`;

  const headerCells = Array.from({ length: 8 }, () => (
    '<th scope="col"><span class="quick-view-skeleton quick-view-skeleton--table-head" aria-hidden="true"></span></th>'
  )).join('');
  const bodyRows = Array.from({ length: 2 }, () => (
    `<tr class="quick-view-size-selector__row"><td colspan="8"><span class="quick-view-skeleton quick-view-skeleton--table-row" aria-hidden="true"></span></td></tr>`
  )).join('');

  const sizes = kind === 'table'
    ? `
<div class="quick-view-size-selector quick-view-loading-skeleton" data-qvm-loading-skeleton data-size-selector aria-busy="true">
  <div class="quick-view-size-selector__header">
    <span class="quick-view-size-selector__heading">
      Available Variants
      <span class="quick-view-skeleton quick-view-skeleton--count" aria-hidden="true"></span>
    </span>
  </div>
  <p class="visually-hidden" role="status">Loading product details</p>
  <div class="quick-view-variant-scroll-box" aria-hidden="true">
    <table class="quick-view-variant-table">
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  </div>
</div>`
    : `
<div class="quick-view-size-selector quick-view-loading-skeleton quick-view-loading-skeleton--neutral" data-qvm-loading-skeleton data-size-selector aria-busy="true">
  <p class="visually-hidden" role="status">Loading product details</p>
  <span class="quick-view-skeleton quick-view-skeleton--table-row" aria-hidden="true"></span>
  <span class="quick-view-skeleton quick-view-skeleton--table-row" aria-hidden="true"></span>
</div>`;

  return { image, info, sizes, cta };
}

/**
 * Full-modal placeholder markup shown while product JSON / cart sync loads.
 * Layout follows `isMatrixProduct`: matrix skeleton, variants-table skeleton, or a neutral block.
 * @param {'matrix' | 'table' | 'unknown'} [layout]
 * @returns {{ image: string, info: string, sizes: string, cta: string }}
 */
function buildQuickViewLoadingSkeleton(layout) {
  if (layout !== 'matrix') {
    return buildNonMatrixQuickViewLoadingSkeleton(layout === 'table' ? 'table' : 'unknown');
  }
  const lengthLabels = ['16"', '18"', '20"', '22"', '24"', '26"', '28"', '30"'];
  const lengthCount = lengthLabels.length;
  const widthRowCount = 2;

  const headCells = lengthLabels
    .map((length) => `<th scope="col" class="quick-view-chain-matrix__length">${escapeAttr(length)}</th>`)
    .join('');

  const matrixCell = `
<td class="quick-view-chain-matrix__cell bulk-order-matrix__variant-cell quick-view-chain-matrix__cell--pending" aria-hidden="true">
  <div class="quick-view-chain-matrix__variant quick-view-chain-matrix__variant--skeleton">
    <span class="quick-view-skeleton quick-view-skeleton--matrix-status"></span>
    <span class="quick-view-skeleton quick-view-skeleton--matrix-qty"></span>
    <span class="quick-view-skeleton quick-view-skeleton--matrix-weight"></span>
  </div>
</td>`;

  const bodyRows = Array.from({ length: widthRowCount }, () => {
    const cells = Array.from({ length: lengthCount }, () => matrixCell).join('');
    return `<tr>
  <th scope="row" class="quick-view-chain-matrix__width" aria-hidden="true">
    <span class="quick-view-skeleton quick-view-skeleton--matrix-width"></span>
  </th>
  ${cells}
</tr>`;
  }).join('');

  const thumbs = Array.from({ length: 4 }, () =>
    '<span class="quick-view-skeleton quick-view-skeleton--thumb" aria-hidden="true"></span>'
  ).join('');

  return {
    image: `
<div class="quick-view-image quick-view-image--horizontal quick-view-loading-skeleton" data-qvm-loading-skeleton aria-hidden="true">
  <div class="quick-view-image__thumbs quick-view-image__thumbs--horizontal" role="presentation">
    ${thumbs}
  </div>
</div>`,
    info: `
<div class="quick-view-product-info quick-view-product-info--chain quick-view-loading-skeleton" data-qvm-loading-skeleton>
  <button type="button" class="quick-view-product-info__close" data-quick-modal-close aria-label="Close">
    <span aria-hidden="true">×</span>
  </button>
  <h2 class="visually-hidden" id="QuickViewModalTitle">Loading product details</h2>
  <span class="quick-view-skeleton quick-view-skeleton--collection" aria-hidden="true"></span>
  <span class="quick-view-skeleton quick-view-skeleton--title" aria-hidden="true"></span>
  <span class="quick-view-skeleton quick-view-skeleton--title quick-view-skeleton--title-line2" aria-hidden="true"></span>
  <div class="quick-view-product-info__badges" aria-hidden="true">
    <span class="quick-view-skeleton quick-view-skeleton--badge"></span>
    <span class="quick-view-skeleton quick-view-skeleton--badge quick-view-skeleton--badge-wide"></span>
  </div>
</div>`,
    sizes: `
<div class="quick-view-size-selector quick-view-size-selector--chain chain-bulk-order-matrix quick-view-loading-skeleton" data-qvm-loading-skeleton data-size-selector aria-busy="true">
  <div class="quick-view-size-selector__header order-sheet-details-chain-matrix__section-header bulk-matrix-section-header">
    <div class="bulk-matrix-section-header__main">
      <h2 class="quick-view-size-selector__matrix-title bulk-matrix-title">Bulk Ordering Matrix</h2>
      <span class="bulk-matrix-variant-count"><span class="quick-view-skeleton quick-view-skeleton--count" aria-hidden="true"></span></span>
    </div>
  </div>
  <p class="visually-hidden" role="status">Loading product details</p>
  <div class="quick-view-chain-matrix__scroll" aria-hidden="true">
    <table
      class="quick-view-chain-matrix"
      aria-hidden="true"
      data-matrix-length-count="${lengthCount}"
      style="--matrix-length-count: ${lengthCount}"
    >
      <colgroup>
        <col class="quick-view-chain-matrix__col-axis">
        <col class="quick-view-chain-matrix__col-length" span="${lengthCount}">
      </colgroup>
      <thead>
        <tr>
          <th scope="col" rowspan="2" class="quick-view-chain-matrix__corner quick-view-chain-matrix__corner-row1"><span class="quick-view-chain-matrix__corner-label">WIDTH (MM)</span></th>
          <th scope="col" colspan="${lengthCount}" class="quick-view-chain-matrix__length-group">Length (inches)</th>
        </tr>
        <tr>
          ${headCells}
        </tr>
      </thead>
      <tbody class="quick-view-chain-matrix__body">${bodyRows}</tbody>
    </table>
  </div>
</div>`,
    cta: `
<div class="quick-view-add-to-cart quick-view-loading-skeleton" data-qvm-loading-skeleton data-quick-view-cta aria-hidden="true">
  <div class="quick-view-add-to-cart__summary">
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Selected Variants:</span>
      <span class="quick-view-skeleton quick-view-skeleton--stat-value"></span>
    </div>
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Total Quantity:</span>
      <span class="quick-view-skeleton quick-view-skeleton--stat-value"></span>
    </div>
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Total Weight:</span>
      <span class="quick-view-skeleton quick-view-skeleton--stat-value"></span>
    </div>
  </div>
  <div class="quick-view-add-to-cart__actions">
    <span class="quick-view-skeleton quick-view-skeleton--cta"></span>
  </div>
</div>`,
  };
}

/**
 * @param {object} product
 * @returns {string}
 */
function quickViewDisplayTitle(product) {
  const merged =
    product && product.merged_product_title != null ? String(product.merged_product_title).trim() : '';
  if (merged) return merged;
  return product && product.title != null ? String(product.title) : '';
}

function buildProductInfoHtml(product) {
  const isChain = isChainProduct(product);
  const isBracelet = isBraceletProduct(product);
  const isMatrix = isChain || isBracelet;
  let vendorLabel = 'COLLECTION';
  if (isMatrix) {
    vendorLabel = 'A-ONE GOLD & DIAMONDS LLC';
  } else if (product.vendor) {
    vendorLabel = String(product.vendor).toUpperCase();
  } else if (product.collections && product.collections[0] && product.collections[0].title) {
    vendorLabel = String(product.collections[0].title).toUpperCase();
  }

  let categoryLabel = product.type && String(product.type).trim() ? String(product.type).trim() : '';
  if (!categoryLabel && product.collections && product.collections[0] && product.collections[0].title) {
    categoryLabel = String(product.collections[0].title);
  }
  if (!categoryLabel) {
    if (isBracelet) categoryLabel = 'Bracelets';
    else if (isChain) categoryLabel = 'Chains';
    else categoryLabel = 'Product';
  }

  // Do not render Shopify In Stock / Out of Stock. WMS hydrates AVAILABLE / COMING SOON.
  const badges = `<span class="quick-view-product-info__badge quick-view-product-info__badge--wms" data-qvm-wms-badge data-wms-status-badge aria-live="polite">
        <span class="quick-view-product-info__badge-dot" aria-hidden="true"></span>
        CHECKING AVAILABILITY
      </span>`;

  return `
<div class="quick-view-product-info${isMatrix ? ' quick-view-product-info--chain' : ''}">
  <button type="button" class="quick-view-product-info__close" data-quick-modal-close aria-label="Close">
    <span aria-hidden="true">×</span>
  </button>
  <p class="quick-view-product-info__collection">${escapeAttr(vendorLabel)}</p>
  <h2 class="quick-view-product-info__title" id="QuickViewModalTitle">${escapeAttr(quickViewDisplayTitle(product))}</h2>
  <div class="quick-view-product-info__badges">
    <span class="quick-view-product-info__category">${escapeAttr(categoryLabel)}</span>
    ${badges}
  </div>
</div>`;
}

/**
 * @param {object[]} variants
 * @returns {{ variant: object, index: number }[]}
 */
function sortVariantsForDisplay(variants) {
  return (variants || []).map((variant, index) => ({ variant, index }));
}

/**
 * @param {object} variant
 * @returns {string}
 */
function formatMinWeight(variant) {
  const w = variant && variant.weight != null ? Number(variant.weight) : NaN;
  if (Number.isFinite(w) && w > 0) {
    const rawUnit = variant.weight_unit ? String(variant.weight_unit) : 'g';
    const unit = normalizeKey(rawUnit || 'g');
    if (unit === 'kg') return `${(w * 1000).toFixed(2)}g`;
    return unit === 'g' ? `${w.toFixed(2)}g` : `${w.toFixed(2)} ${rawUnit}`;
  }
  return '—';
}

/**
 * @param {object} variant
 * @returns {number}
 */
function variantWeightGrams(variant) {
  const w = variant && variant.weight != null ? Number(variant.weight) : NaN;
  if (!Number.isFinite(w) || w <= 0) return 0;
  const unit = normalizeKey(variant.weight_unit || 'g');
  if (unit === 'kg') return w * 1000;
  return w;
}

/**
 * @param {number} grams
 * @returns {string}
 */
function formatGrams(grams) {
  if (!Number.isFinite(grams) || grams <= 0) return '—';
  return `${grams.toFixed(2)}g`;
}

const QVM_STOCK_SECTION = 'quick-view-variant-stock';

/**
 * @param {unknown} data
 * @returns {data is Record<string, number>}
 */
function isFlatStockMap(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  if ('product' in data || 'sections' in data) return false;
  return Object.values(data).every((v) => typeof v === 'number' || typeof v === 'string');
}

/**
 * Normalize product.qty / section stock JSON into variantId → qty.
 * Ignores meta keys (_total_inventory, _sku, _min_weight_grams).
 * @param {unknown} data
 * @returns {Record<string, number>}
 */
function normalizeQtyViewStockMap(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  /** @type {Record<string, number>} */
  const out = {};
  Object.keys(/** @type {Record<string, unknown>} */ (data)).forEach((key) => {
    if (String(key).charAt(0) === '_') return;
    const n = Number(/** @type {Record<string, unknown>} */ (data)[key]);
    if (!Number.isFinite(n)) return;
    out[String(key)] = Math.max(0, Math.floor(n));
  });
  return out;
}

/**
 * Shopify inventory for one variant (never WMS).
 * Prefers preserved shopify_inventory_quantity set at product load.
 * @param {object} variant
 * @returns {number}
 */
function getShopifyVariantInventoryQty(variant) {
  if (!variant || typeof variant !== 'object') return 0;
  const preserved = Number(variant.shopify_inventory_quantity);
  if (Number.isFinite(preserved) && preserved >= 0) return Math.floor(preserved);
  const qty = Number(variant.inventory_quantity);
  if (Number.isFinite(qty) && qty >= 0) return Math.floor(qty);
  return 0;
}

/**
 * Matrix / table stock status from Shopify qty only.
 * Weight label still accepts WMS min weight when provided.
 * @param {unknown} shopifyQty
 * @param {{ effectiveMinWeight?: unknown }} [options]
 * @returns {{
 *   key: 'coming-soon'|'available',
 *   label: string,
 *   quantity: number,
 *   minimumWeightLabel?: string,
 *   quantityEnabled?: boolean,
 *   weightSelectionEnabled?: boolean
 * }}
 */
function getShopifyStockStatus(shopifyQty, options = {}) {
  const quantity = Math.max(0, Math.floor(Number(shopifyQty) || 0));
  const inventoryApi =
    /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
  const minimumWeightLabel =
    inventoryApi && typeof inventoryApi.formatMinimumWeightLabel === 'function'
      ? inventoryApi.formatMinimumWeightLabel(options.effectiveMinWeight)
      : Number(options.effectiveMinWeight) > 0
        ? `Min wt: ${Number(options.effectiveMinWeight).toFixed(2)}g`
        : 'Min wt: N/A';

  if (quantity > 0) {
    return {
      key: 'available',
      label: `IN-STOCK: ${quantity}`,
      quantity,
      minimumWeightLabel,
      quantityEnabled: true,
      weightSelectionEnabled: true,
    };
  }

  return {
    key: 'coming-soon',
    label: 'COMING SOON',
    quantity: 0,
    minimumWeightLabel: 'Min wt: N/A',
    quantityEnabled: false,
    weightSelectionEnabled: false,
  };
}

/**
 * @param {string} html
 * @returns {Record<string, number>}
 */
function parseStockMapFromSectionHtml(html) {
  if (!html) return {};
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('[data-qvm-variant-stock]');
    if (!script) return {};
    const parsed = JSON.parse(script.textContent || '{}');
    const normalized = normalizeQtyViewStockMap(parsed);
    if (Object.keys(normalized).length) return normalized;
    return isFlatStockMap(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * @param {string} handle
 * @param {AbortSignal} [signal]
 * @returns {Promise<Record<string, number>>}
 */
async function fetchStockViaQtyView(handle, signal) {
  try {
    const url = `/products/${encodeURIComponent(handle)}?view=qty`;
    const res = await fetch(url, { signal });
    if (!res.ok) return {};
    const text = (await res.text()).trim();
    if (!text) return {};

    if (text.startsWith('<')) {
      return parseStockMapFromSectionHtml(text);
    }

    const data = JSON.parse(text);
    const fromQtyView = normalizeQtyViewStockMap(data);
    if (Object.keys(fromQtyView).length) return fromQtyView;
    if (isFlatStockMap(data)) return data;
    if (data?.sections?.[QVM_STOCK_SECTION]) {
      return parseStockMapFromSectionHtml(data.sections[QVM_STOCK_SECTION]);
    }
    return {};
  } catch (err) {
    if (err && err.name === 'AbortError') throw err;
    return {};
  }
}

/**
 * @param {string} handle
 * @param {AbortSignal} [signal]
 * @returns {Promise<Record<string, number>>}
 */
async function fetchStockViaSectionApi(handle, signal) {
  try {
    const url = `/products/${encodeURIComponent(handle)}?sections=${QVM_STOCK_SECTION}`;
    const res = await fetch(url, { signal });
    if (!res.ok) return {};
    const data = await res.json();
    return parseStockMapFromSectionHtml(data?.sections?.[QVM_STOCK_SECTION]);
  } catch (err) {
    if (err && err.name === 'AbortError') throw err;
    return {};
  }
}

/**
 * Fetch Shopify variant inventory via product.qty (and section fallback).
 * @param {string} handle
 * @param {AbortSignal} [signal]
 * @returns {Promise<Record<string, number>>}
 */
async function fetchVariantStockMap(handle, signal) {
  const safe = String(handle || '').trim();
  if (!safe) return {};
  const fromQtyView = await fetchStockViaQtyView(safe, signal);
  if (Object.keys(fromQtyView).length) return fromQtyView;
  return fetchStockViaSectionApi(safe, signal);
}

/**
 * @param {object} product
 * @param {Record<string, number>} stockMap
 */
function applyStockMapToProduct(product, stockMap) {
  if (!product?.variants || !stockMap) return;
  product.variants.forEach((variant) => {
    const key = String(variant.id);
    if (!Object.prototype.hasOwnProperty.call(stockMap, key)) return;
    const qty = Number(stockMap[key]);
    if (!Number.isFinite(qty)) return;
    const safeQty = Math.max(0, Math.floor(qty));
    variant.inventory_quantity = safeQty;
    variant.shopify_inventory_quantity = safeQty;
    variant.inventory_management = 'shopify';
  });
}

/**
 * Stock cell from cart-aware effective availability (never Shopify inventory).
 * @param {{ key?: string, quantity?: number | null }} stockStatus
 * @returns {string}
 */
function buildStockCell(stockStatus) {
  if (!stockStatus || stockStatus.key === 'loading') {
    return '<span class="quick-view-skeleton quick-view-skeleton--stock" aria-hidden="true"></span>';
  }
  if (stockStatus.key === 'error') {
    return '<span class="quick-view-stock-qty">AVAILABILITY UNAVAILABLE</span>';
  }
  if (stockStatus.key === 'available' && Number(stockStatus.quantity) > 0) {
    return `<span class="quick-view-stock-qty">${Math.floor(Number(stockStatus.quantity))}</span>`;
  }
  if (stockStatus.key === 'all-in-cart') {
    // Remaining is 0 — never fall back to raw warehouse qty (0 is valid).
    return `<span class="quick-view-stock-qty">0</span>`;
  }
  return `<span class="quick-view-stock-qty">0</span>`;
}

/**
 * @param {boolean} available
 * @param {boolean} isModalOnly
 * @param {string} [inputId]
 * @param {number|string} [inventoryQty]
 * @returns {string}
 */
function buildQtyCellHtml(available, isModalOnly, inputId = '', inventoryQty = '') {
  const disabled = !available ? ' disabled' : '';
  const idAttr = inputId ? ` id="${escapeAttr(inputId)}"` : '';
  const maxAttr =
    inventoryQty !== '' && Number(inventoryQty) >= 0
      ? ` max="${inventoryQty}" data-variant-inventory-qty="${inventoryQty}"`
      : '';

  if (isModalOnly) {
    return `<td class="quick-view-variant-table__qty">
      <div class="quick-view-qty order-sheet-details-qty quantity-control" role="group" aria-label="Quantity">
        <button type="button" class="order-sheet-details-qty__btn quick-view-qty__btn" data-qvm-qty-minus aria-label="Decrease quantity"${disabled}>−</button>
        <input type="number" class="order-sheet-details-qty__input quick-view-qty__value"${idAttr} data-qvm-qty-display-input value="0" min="0" step="1" inputmode="numeric" aria-label="Quantity"${disabled}>
        <button type="button" class="order-sheet-details-qty__btn quick-view-qty__btn" data-qvm-qty-plus aria-label="Increase quantity"${disabled}>+</button>
      </div>
    </td>`;
  }

  return `<td class="quick-view-variant-table__qty">
    <div class="quick-view-qty order-sheet-details-qty quantity-control" role="group" aria-label="Quantity">
      <button type="button" class="order-sheet-details-qty__btn quick-view-qty__btn" data-details-qty-minus aria-label="Decrease quantity"${disabled}>−</button>
      <input type="number" class="order-sheet-details-qty__input quick-view-qty__value"${idAttr} data-details-qty-input data-quick-view-quantity value="0" min="0" step="1"${maxAttr} inputmode="numeric" aria-label="Quantity"${disabled}>
      <button type="button" class="order-sheet-details-qty__btn quick-view-qty__btn" data-details-qty-plus aria-label="Increase quantity"${disabled}>+</button>
    </div>
  </td>`;
}

/**
 * @param {object} product
 * @param {object} variant
 * @param {string[]} optionNames
 * @returns {string}
 */
function buildOptionCells(product, variant, optionNames) {
  const hasOnlyDefault =
    product.has_only_default_variant === true ||
    (optionNames.length === 1 &&
      (!variant.option1 || variant.option1 === 'Default Title') &&
      !variant.option2 &&
      !variant.option3);

  if (hasOnlyDefault || optionNames.length === 0) {
    const titleText = variantDisplayTitle(variant);
    const label = titleText && titleText !== 'Default Title' ? titleText : optionNames[0] || 'Variant';
    return `<td class="quick-view-variant-table__option">${escapeAttr(label)}</td>`;
  }

  return optionNames
    .map((_, i) => {
      const key = /** @type {'option1'|'option2'|'option3'} */ (`option${i + 1}`);
      const val = variant[key];
      return `<td class="quick-view-variant-table__option">${escapeAttr(val != null && String(val).trim() ? String(val) : '—')}</td>`;
    })
    .join('');
}

/**
 * Live products-batch availability for one Quick View SKU.
 * Pending / error keeps the existing loading presentation and must not be treated as zero.
 * @param {object} variant
 * @param {object} product
 * @param {Map<string, unknown>} wmsBySku
 * @param {boolean} hasWmsData
 * @param {{ wmsLoading?: boolean, wmsError?: boolean }} [options]
 */
function isShopifyQtyKnown(variant) {
  if (!variant || typeof variant !== 'object') return false;
  if (Number.isFinite(Number(variant.shopify_inventory_quantity))) return true;
  return Number.isFinite(Number(variant.inventory_quantity));
}

function readQuickViewVariantStock(variant, product, wmsBySku, hasWmsData, options = {}) {
  const wmsLoading = options.wmsLoading === true;
  const wmsError = options.wmsError === true;
  const resolved = resolveMatrixAvailableQuantity(
    {
      ...variant,
      product_id: variant?.product_id || variant?.productId || product?.id,
      productId: variant?.productId || variant?.product_id || product?.id,
    },
    wmsBySku,
    hasWmsData
  );
  const shopifyKnown = isShopifyQtyKnown(variant);
  const shopifyQty = shopifyKnown ? getShopifyVariantInventoryQty(variant) : 0;
  const weightPending = !wmsError && (wmsLoading || resolved.loading === true || resolved.fromWms !== true);
  const shopifyStatus = shopifyKnown
    ? getShopifyStockStatus(shopifyQty, {
        effectiveMinWeight: weightPending ? null : resolved.minWeight,
      })
    : {
        key: 'loading',
        label: '',
        quantity: 0,
        minimumWeightLabel: '',
        quantityEnabled: false,
        weightSelectionEnabled: false,
      };

  return {
    pending: weightPending,
    shopifyPending: !shopifyKnown,
    confirmedOut: shopifyKnown && shopifyQty <= 0,
    stockStatus: shopifyStatus,
    shopifyQty,
    shopifyKnown,
    useLiveStock: false,
  };
}

/**
 * Neutral disabled matrix cell. Keeps Width × Length position, with no controls.
 * @returns {string}
 */
function buildUnavailableMatrixCellHtml() {
  return `<td class="quick-view-chain-matrix__cell quick-view-chain-matrix__cell--sold-out quick-view-chain-matrix__cell--stock-empty"><div class="quick-view-chain-matrix__variant quick-view-chain-matrix__variant--sold-out is-stock-empty" aria-hidden="true"></div></td>`;
}

/**
 * @param {object} product
 * @param {{ wmsLoading?: boolean, wmsError?: boolean }} [options]
 * @returns {string}
 */
function buildSizeSelectorHtml(product, options = {}) {
  if (isMatrixProduct(product)) {
    return buildChainMatrixSelectorHtml(product, options);
  }

  const wmsLoading = options.wmsLoading === true;
  const wmsError = options.wmsError === true;
  const variants = product.variants || [];
  const optionNames = getOptionNames(product);
  const optionLabel = optionNames[0] || 'Size';
  const headerCells =
    optionNames.length > 0
      ? optionNames.map((n) => `<th scope="col">${escapeAttr(n)}</th>`).join('')
      : `<th scope="col">${escapeAttr(optionLabel)}</th>`;

  const { wmsBySku, hasWmsData } = buildMatrixWmsBySkuMap(product);
  const rowHtmlList = sortVariantsForDisplay(variants)
    .map(({ variant }) => {
      const sku = variant.sku != null ? String(variant.sku).trim() : '';
      const live = readQuickViewVariantStock(variant, product, wmsBySku, hasWmsData, {
        wmsLoading,
        wmsError,
      });
      // Display filter only. product.variants is left unchanged.
      // Shopify qty decides visibility. products-batch only fills weight data later.
      if (!live.shopifyPending && live.confirmedOut) return '';
      const stockStatus = live.stockStatus;
      const shopifyQty = live.shopifyQty;
      const showWeightSkeleton = live.pending;
      const showSkeleton = live.shopifyPending;
      const invQty = live.shopifyKnown ? shopifyQty : 0;
      if (live.shopifyKnown) {
        variant.inventory_quantity = shopifyQty;
        variant.shopify_inventory_quantity = shopifyQty;
      }
      const rowAvailable = stockStatus.key === 'available' && Number(stockStatus.quantity) > 0;
      const rowClass = rowAvailable
        ? 'quick-view-size-selector__row'
        : 'quick-view-size-selector__row quick-view-size-selector__row--sold-out';
      const isModalOnly = !!sku;
      const invAttr = live.shopifyKnown
        ? ` data-variant-inventory-qty="${invQty}" data-shopify-inventory-qty="${shopifyQty}"`
        : ` data-variant-inventory-qty="0"`;
      const wmsStatusAttr = live.shopifyPending || showWeightSkeleton
        ? 'loading'
        : stockStatus.key === 'available'
          ? 'available'
          : 'coming-soon';
      const minWeightIdleLabel = formatMatrixMinWeightIdleLabel(variant, stockStatus);
      let weightAction = '<span class="order-sheet-details-variant-table__weight-na" aria-hidden="true">—</span>';
      if (sku) {
        const disabled = !rowAvailable ? ' disabled' : '';
        weightAction = showWeightSkeleton
          ? `
<span class="quick-view-skeleton quick-view-skeleton--button" aria-hidden="true"></span>
<input type="hidden" data-details-qty-input data-details-qty-modal-only data-quick-view-quantity value="0">`
          : `
<button type="button" class="order-sheet-details-variant-table__weight-btn quick-view-size-selector__weight-btn" data-details-weight-open aria-haspopup="dialog" data-weight-idle-label="${escapeAttr(minWeightIdleLabel)}"${disabled}>
  <span data-details-weight-label>${escapeAttr(minWeightIdleLabel)}</span>
</button>
<input type="hidden" data-details-qty-input data-details-qty-modal-only data-quick-view-quantity value="0" max="${invQty}">`;
      }
      // WMS-only min weight. Never fall back to Shopify variant.weight while waiting or after resolve.
      const minWeightCell = showWeightSkeleton
        ? 'Checking...'
        : stockStatus.minimumWeightLabel &&
            !String(stockStatus.minimumWeightLabel).includes('N/A')
          ? escapeAttr(String(stockStatus.minimumWeightLabel).replace(/^Min wt:\s*/i, ''))
          : 'N/A';
      const stockCell = buildStockCell(stockStatus);
      return `
<tr class="${rowClass}" data-details-variant-row data-variant-row data-variant-id="${variant.id}" data-variant-sku="${escapeAttr(sku)}" data-variant-available="${rowAvailable ? 'true' : 'false'}" data-wms-status="${wmsStatusAttr}" data-qvm-inventory-pending="${showSkeleton ? 'true' : 'false'}"${invAttr}>
  ${buildOptionCells(product, variant, optionNames)}
  <td class="quick-view-variant-table__sku">${escapeAttr(sku || '—')}</td>
  <td class="quick-view-variant-table__min-weight">${minWeightCell}</td>
  <td class="quick-view-variant-table__stock">${stockCell}</td>
  ${buildQtyCellHtml(rowAvailable, isModalOnly, `QvmQty-${variant.id}`, invQty)}
  <td class="quick-view-size-selector__actions quick-view-variant-table__weight">${weightAction}</td>
</tr>`;
    })
    .filter(Boolean);
  const rows = rowHtmlList.join('');
  const visibleCount = rowHtmlList.length;
  const optionColumnCount = optionNames.length > 0 ? optionNames.length : 1;
  const emptyStockRow =
    variants.length > 0 && rowHtmlList.length === 0
      ? `<tr class="quick-view-variant-table__empty"><td colspan="${optionColumnCount + 5}">No variants currently in stock.</td></tr>`
      : '';

  const inventoryNotice = wmsLoading
    ? `<p class="quick-view-size-selector__inventory-note" role="status" aria-live="polite"><span class="visually-hidden">Loading inventory details</span></p>`
    : wmsError
      ? `<div class="quick-view-size-selector__inventory-error" role="alert"><span>Unable to load inventory.</span><button type="button" class="quick-view-size-selector__retry-btn" data-qvm-wms-retry>Retry</button></div>`
      : '';

  return `
<div class="quick-view-size-selector" data-size-selector aria-busy="${wmsLoading ? 'true' : 'false'}">
  <div class="quick-view-size-selector__header">
    <span class="quick-view-size-selector__heading">
      Available Variants
      <span class="quick-view-size-selector__count">(${visibleCount})</span>
    </span>
  </div>
  ${inventoryNotice}
  <div class="quick-view-variant-scroll-box" aria-live="polite">
    <table class="quick-view-variant-table">
      <thead>
        <tr>
          ${headerCells}
          <th scope="col" class="quick-view-variant-table__col-sku">SKU</th>
          <th scope="col" class="quick-view-variant-table__col-weight-min">Min Weight</th>
          <th scope="col" class="quick-view-variant-table__col-stock">Available Stock</th>
          <th scope="col" class="quick-view-variant-table__col-qty">Order Qty</th>
          <th scope="col" class="quick-view-variant-table__col-weight">Weight</th>
        </tr>
      </thead>
      <tbody class="quick-view-size-selector__list" role="rowgroup">${rows || emptyStockRow}</tbody>
    </table>
  </div>
</div>`;
}

/**
 * @param {Record<string, Record<string, object>>} map
 * @param {string[]} widths
 * @param {string[]} lengths
 */
function countMatrixVariants(map, widths, lengths) {
  let count = 0;
  for (const width of widths) {
    for (const length of lengths) {
      if (map[width]?.[length]) count += 1;
    }
  }
  return count;
}

/** @param {number} count */
function formatMatrixVariantCount(count) {
  const total = Math.max(0, Math.floor(Number(count) || 0));
  return `${total} ${total === 1 ? 'Variant' : 'Variants'}`;
}

/**
 * @param {object} variant
 * @param {'in-stock'|'sold-out'} stockLevel
 */
function getMatrixAvailableQuantity(variant, stockLevel) {
  if (stockLevel !== 'in-stock') return null;
  const qty = Number(variant.inventory_quantity);
  if (!Number.isFinite(qty)) {
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn('[chain-matrix] missing inventory_quantity for variant', variant.id);
    }
    return null;
  }
  const normalized = Math.max(0, Math.floor(qty));
  if (normalized <= 0) return null;
  return normalized;
}

/**
 * Deterministic matrix stock status from raw + effective quantities (WMS-first, cart-aware).
 * Coming Soon only when raw WMS qty is zero.
 * Confirmed WMS zero must never render as AVAILABILITY UNAVAILABLE.
 * @param {unknown} value
 * @param {{ rawQuantity?: unknown, effectiveMinWeight?: unknown, isLoading?: boolean, wmsRequestFailed?: boolean }} [options]
 * @returns {{
 *   key: 'coming-soon'|'available'|'all-in-cart'|'loading'|'error',
 *   label: string,
 *   quantity: number,
 *   minimumWeightLabel?: string,
 *   quantityEnabled?: boolean,
 *   weightSelectionEnabled?: boolean
 * }}
 */
function getWmsStockStatus(value, options = {}) {
  const inventoryApi = /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
  const quantity = Number.isFinite(Number(value))
    ? Math.max(0, Math.floor(Number(value)))
    : 0;
  const rawQuantity = Number.isFinite(Number(options.rawQuantity))
    ? Math.max(0, Math.floor(Number(options.rawQuantity)))
    : quantity;

  if (inventoryApi && typeof inventoryApi.resolveVariantAvailabilityState === 'function') {
    const cartReady =
      typeof inventoryApi.isCartStateReady === 'function' ? inventoryApi.isCartStateReady() : true;
    const resolved = inventoryApi.resolveVariantAvailabilityState({
      rawWmsQty: rawQuantity,
      effectiveQty: quantity,
      effectiveMinWeight: options.effectiveMinWeight,
      isLoading: options.isLoading === true,
      wmsRequestFailed: options.wmsRequestFailed === true,
      wmsResolved: options.isLoading !== true && options.wmsRequestFailed !== true,
      cartResolved: cartReady,
    });
    return {
      key: resolved.state,
      label: resolved.label,
      quantity: resolved.availableQuantity,
      displayQuantity:
        resolved.state === 'all-in-cart'
          ? 0
          : resolved.displayQuantity != null
            ? resolved.displayQuantity
            : resolved.availableQuantity,
      rawAvailableCount: resolved.rawAvailableCount,
      remainingQuantity: resolved.availableQuantity,
      minimumWeightLabel: resolved.minimumWeightLabel,
      quantityEnabled: resolved.quantityEnabled,
      weightSelectionEnabled: resolved.weightSelectionEnabled,
    };
  }

  if (options.isLoading === true) {
    return { key: 'loading', label: 'CHECKING AVAILABILITY', quantity: 0, minimumWeightLabel: 'Min wt: N/A' };
  }
  if (options.wmsRequestFailed === true) {
    return { key: 'error', label: 'UNAVAILABLE', quantity: 0, minimumWeightLabel: 'Min wt: N/A' };
  }

  const statusKey =
    inventoryApi && typeof inventoryApi.getStorefrontStatus === 'function'
      ? inventoryApi.getStorefrontStatus(rawQuantity, quantity)
      : quantity > 0
        ? 'AVAILABLE'
        : rawQuantity > 0
          ? 'ALL_IN_CART'
          : 'COMING_SOON';

  if (statusKey === 'COMING_SOON') {
    return {
      key: 'coming-soon',
      label: 'COMING SOON',
      quantity: 0,
      minimumWeightLabel: 'Min wt: N/A',
      quantityEnabled: false,
      weightSelectionEnabled: false,
    };
  }

  if (statusKey === 'ALL_IN_CART') {
    return {
      key: 'all-in-cart',
      label: 'IN-STOCK: 0',
      quantity: 0,
      displayQuantity: 0,
      rawAvailableCount: rawQuantity,
      remainingQuantity: 0,
      minimumWeightLabel: 'Min wt: N/A',
      quantityEnabled: true,
      weightSelectionEnabled: true,
    };
  }

  return {
    key: 'available',
    label: `IN-STOCK: ${quantity}`,
    quantity,
    minimumWeightLabel:
      inventoryApi && typeof inventoryApi.formatMinimumWeightLabel === 'function'
        ? inventoryApi.formatMinimumWeightLabel(options.effectiveMinWeight)
        : Number(options.effectiveMinWeight) > 0
          ? `Min wt: ${Number(options.effectiveMinWeight).toFixed(2)}g`
          : 'Min wt: N/A',
    quantityEnabled: true,
    weightSelectionEnabled: true,
  };
}

/**
 * Build a SKU → WMS item lookup for one product. Reuses cached batch data / shared store.
 * @param {object} product
 * @returns {{ wmsBySku: Map<string, unknown>, hasWmsData: boolean }}
 */
function buildMatrixWmsBySkuMap(product) {
  /** @type {Map<string, unknown>} */
  const wmsBySku = new Map();
  const store = /** @type {any} */ (window).AOneProductWeights;
  /** @type {unknown[]} */
  const familyItems = [];
  familyProductIds(product).forEach((id) => {
    const productData = store?.products?.get?.(id);
    const items = Array.isArray(productData?.skus)
      ? productData.skus
      : Array.isArray(productData?.items)
        ? productData.items
        : null;
    if (items) familyItems.push(...items);
  });

  if (familyItems.length) {
    familyItems.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const key = String(/** @type {Record<string, unknown>} */ (item).sku || '')
        .trim()
        .toLowerCase();
      if (!key) return;
      wmsBySku.set(key, item);
    });
    return { wmsBySku, hasWmsData: true };
  }

  const shared = getSharedWeightStore();
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  let found = 0;
  variants.forEach((variant) => {
    const sku = normalizeSku(variant?.sku != null ? String(variant.sku) : '');
    if (!sku) return;
    const key = sku.toLowerCase();
    const item = shared.rawBySku[key];
    if (item == null) return;
    wmsBySku.set(key, item);
    found += 1;
  });

  return { wmsBySku, hasWmsData: found > 0 };
}

/**
 * Resolve available quantity for a matrix cell: WMS by exact SKU when present, else safe fallback.
 * Uses extractAvailableQtyFromNode (prefers established `count` field).
 * @param {object} variant
 * @param {Map<string, unknown>} wmsBySku
 * @param {boolean} hasWmsData
 * @returns {{ quantity: number, fromWms: boolean, wmsMatched: boolean }}
 */
function resolveMatrixAvailableQuantity(variant, wmsBySku, hasWmsData) {
  const sku = normalizeSku(variant?.sku != null ? String(variant.sku) : '');
  const normalizedSku = sku.toLowerCase();
  const inventoryApi = /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
  const productId = normalizeShopifyId(variant?.product_id || variant?.productId);

  if (inventoryApi && typeof inventoryApi.getSkuAvailability === 'function' && productId && sku) {
    const availability = inventoryApi.getSkuAvailability(productId, sku);
    if (
      availability &&
      (availability.loading ||
        availability.status === 'loading' ||
        availability.status === 'idle' ||
        (typeof inventoryApi.isCartStateReady === 'function' && !inventoryApi.isCartStateReady()))
    ) {
      return {
        quantity: null,
        rawQuantity: null,
        fromWms: true,
        wmsMatched: false,
        loading: true,
        minWeight: null,
      };
    }
    if (availability && availability.status === 'loaded') {
      const rawQuantity = Math.max(
        0,
        Math.floor(Number(availability.rawAvailableCount ?? availability.count) || 0)
      );
      const effectiveQuantity = Math.max(0, Math.floor(Number(availability.count) || 0));
      // found:false / SKU_NOT_RETURNED after a loaded batch = confirmed zero inventory.
      return {
        quantity: effectiveQuantity,
        rawQuantity: availability.found === false ? 0 : rawQuantity,
        fromWms: true,
        wmsMatched: true,
        minWeight: availability.minWeight,
        storefrontStatus: availability.storefrontStatus,
      };
    }
  }

  if (hasWmsData) {
    if (!normalizedSku) {
      return { quantity: 0, rawQuantity: 0, fromWms: true, wmsMatched: true, minWeight: null };
    }
    const wmsItem = wmsBySku.get(normalizedSku);
    if (!wmsItem) {
      // Product batch resolved but this SKU has no stock entry → Coming Soon, not UNAVAILABLE.
      return { quantity: 0, rawQuantity: 0, fromWms: true, wmsMatched: true, minWeight: null };
    }
    let availableOptions = Array.isArray(/** @type {any} */ (wmsItem).availableOptions)
      ? /** @type {any} */ (wmsItem).availableOptions
      : [];
    const rawOptionsLen = availableOptions.length;
    if (inventoryApi && typeof inventoryApi.filterAvailableOptions === 'function') {
      availableOptions = inventoryApi.filterAvailableOptions(sku, availableOptions);
    }
    const rawQuantityNode = extractAvailableQtyFromNode(wmsItem);
    const rawQuantity = Number.isFinite(Number(rawQuantityNode))
      ? Math.max(0, Math.floor(Number(rawQuantityNode)))
      : rawOptionsLen;
    const effectiveQuantity = availableOptions.length;
    const sourceForMin =
      effectiveQuantity > 0
        ? availableOptions
        : Array.isArray(/** @type {any} */ (wmsItem).rawAvailableOptions)
          ? /** @type {any} */ (wmsItem).rawAvailableOptions
          : Array.isArray(/** @type {any} */ (wmsItem).availableOptions)
            ? /** @type {any} */ (wmsItem).availableOptions
            : [];
    const minWeight =
      sourceForMin
        .map((option) => Number(option?.weight))
        .filter((weight) => Number.isFinite(weight) && weight > 0)
        .sort((a, b) => a - b)[0] ?? null;
    return {
      quantity: effectiveQuantity,
      rawQuantity,
      fromWms: true,
      wmsMatched: true,
      minWeight,
    };
  }

  return { quantity: 0, rawQuantity: 0, fromWms: false, wmsMatched: false, minWeight: null };
}

/**
 * Idle label for the weight button (Select Weight / Edit Weight shared contract).
 * Coming Soon only when the row is not effectively available.
 * @param {object} _variant
 * @param {{ key?: string, quantity?: number, minimumWeightLabel?: string } | null} [_stockStatus]
 * @returns {string}
 */
function formatMatrixMinWeightIdleLabel(_variant, _stockStatus) {
  const stockStatus = _stockStatus || {};
  if (stockStatus?.key === 'loading') return '';
  if (stockStatus?.key === 'all-in-cart') {
    const minLabel = stockStatus.minimumWeightLabel || '';
    if (minLabel && !String(minLabel).includes('N/A')) return String(minLabel);
    return 'Edit weight';
  }
  if (stockStatus?.key !== 'available' || !(Number(stockStatus.quantity) > 0)) {
    return 'Coming Soon';
  }
  const minLabel = stockStatus.minimumWeightLabel || '';
  if (minLabel && !String(minLabel).includes('N/A')) return String(minLabel);
  return 'Select Weight';
}

/**
 * Compact-mode class from unique length-column count (desktop density only).
 * @param {number} lengthCount
 * @returns {string}
 */
function getMatrixLengthCompactClass(lengthCount) {
  const count = Math.max(0, Math.floor(Number(lengthCount) || 0));
  if (count > 12) return ' quick-view-chain-matrix--cols-dense';
  if (count >= 11) return ' quick-view-chain-matrix--cols-extra-compact';
  if (count >= 9) return ' quick-view-chain-matrix--cols-compact';
  return '';
}

/**
 * Compact availability status + info tooltip trigger for a matrix cell.
 * Tooltip shows only the exact Shopify SKU for this width×length variant.
 * @param {object} variant
 * @param {{ key: string, label: string, quantity: number }} stockStatus
 * @returns {string}
 */
function buildMatrixStatusHtml(variant, stockStatus) {
  const statusMod = stockStatus?.key || 'coming-soon';
  const statusText = stockStatus?.label || 'COMING SOON';
  const sku = variant?.sku != null ? String(variant.sku).trim() : '';

  return `
<div class="quick-view-chain-matrix__status bulk-order-matrix__status quick-view-chain-matrix__status--${escapeAttr(statusMod)}" role="status">
  <span class="quick-view-chain-matrix__status-text">${escapeAttr(statusText)}</span>
  <button
    type="button"
    class="quick-view-chain-matrix__info-btn bulk-order-matrix__info-btn"
    data-matrix-info-tip
    aria-label="View variant SKU"
    aria-expanded="false"
    data-tip-sku="${escapeAttr(sku)}"
  >
    <svg class="quick-view-chain-matrix__info-icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.25"></circle>
      <circle cx="8" cy="5.25" r="0.9" fill="currentColor"></circle>
      <path d="M8 7.35v3.9" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"></path>
    </svg>
  </button>
</div>`;
}

/**
 * @param {object} variant
 * @param {number} _lowStockThreshold
 * @returns {'in-stock'|'sold-out'}
 */
function getStockLevel(variant, _lowStockThreshold) {
  const qty = Number(variant.inventory_quantity);
  if (!Number.isFinite(qty) || qty <= 0) return 'sold-out';
  return 'in-stock';
}

/**
 * @param {HTMLElement} scrollEl
 */
function syncQuickViewMatrixStickyHeaderHeights(scrollEl) {
  if (!(scrollEl instanceof HTMLElement)) return;
  const groupHeader = scrollEl.querySelector('.quick-view-chain-matrix__length-group');
  const subHeader = scrollEl.querySelector('.quick-view-chain-matrix__length');
  if (groupHeader instanceof HTMLElement) {
    const mainHeight = Math.ceil(groupHeader.getBoundingClientRect().height);
    if (mainHeight >= 24 && mainHeight <= 80) {
      scrollEl.style.setProperty('--matrix-main-header-height', `${mainHeight}px`);
    } else {
      scrollEl.style.removeProperty('--matrix-main-header-height');
    }
  }
  if (subHeader instanceof HTMLElement) {
    const subHeight = Math.ceil(subHeader.getBoundingClientRect().height);
    if (subHeight >= 24 && subHeight <= 80) {
      scrollEl.style.setProperty('--matrix-sub-header-height', `${subHeight}px`);
    } else {
      scrollEl.style.removeProperty('--matrix-sub-header-height');
    }
  }
}

/**
 * @param {HTMLElement} scrollEl
 */
function bindQuickViewMatrixStickyHeaderSync(scrollEl) {
  if (!(scrollEl instanceof HTMLElement)) return;
  const plpSync = window.plpVariantDetails?.bindChainMatrixStickyHeaderSync;
  if (typeof plpSync === 'function') {
    plpSync(scrollEl);
    return;
  }
  syncQuickViewMatrixStickyHeaderHeights(scrollEl);
}

/**
 * @param {object} product
 * @param {{ wmsLoading?: boolean, wmsError?: boolean }} [options]
 * @returns {string}
 */
function buildChainMatrixSelectorHtml(product, options = {}) {
  const wmsLoading = options.wmsLoading === true;
  const wmsError = options.wmsError === true;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const { widthIdx, lengthIdx, fixedWidth } = resolveChainAxes(product);
  const widthsSet = new Set();
  const lengthsSet = new Set();
  /** @type {Record<string, Record<string, object>>} */
  const map = Object.create(null);

  variants.forEach((variant) => {
    const widthVal = normalizeWidthLabel(
      widthIdx >= 0 ? getVariantOptionValue(variant, widthIdx) || '—' : fixedWidth || '—'
    );
    const lengthVal = getVariantOptionValue(variant, lengthIdx) || '—';
    widthsSet.add(widthVal);
    lengthsSet.add(lengthVal);
    if (!map[widthVal]) map[widthVal] = Object.create(null);
    map[widthVal][lengthVal] = chooseMatrixVariant(map[widthVal][lengthVal], variant);
  });

  const widths = sortMatrixValues(Array.from(widthsSet));
  const lengths = sortMatrixValues(Array.from(lengthsSet));
  if (!widths.length || !lengths.length) {
    return `<div class="quick-view-size-selector" data-size-selector><p class="quick-view-size-selector__heading">No variants available.</p></div>`;
  }

  const totalVariants = countMatrixVariants(map, widths, lengths);
  const variantCountLabel = formatMatrixVariantCount(totalVariants);
  const lengthCount = lengths.length;
  const compactClass = getMatrixLengthCompactClass(lengthCount);

  const headCells = lengths
    .map((length) => `<th scope="col" class="quick-view-chain-matrix__length">${escapeAttr(length)}</th>`)
    .join('');

  const { wmsBySku, hasWmsData } = buildMatrixWmsBySkuMap(product);
  const bodyRows = widths
    .map((width) => {
      const cells = lengths
        .map((length) => {
          const variant = map[width]?.[length];
          if (!variant) {
            return `<td class="quick-view-chain-matrix__cell quick-view-chain-matrix__cell--missing"><div class="quick-view-chain-matrix__cell-empty" aria-hidden="true"></div></td>`;
          }

          const sku = variant.sku != null ? String(variant.sku).trim() : '';
          const live = readQuickViewVariantStock(variant, product, wmsBySku, hasWmsData, {
            wmsLoading,
            wmsError,
          });
          if (live.shopifyPending) {
            return `<td class="quick-view-chain-matrix__cell quick-view-chain-matrix__cell--pending" aria-busy="true"><div class="quick-view-chain-matrix__variant quick-view-chain-matrix__variant--skeleton"><span class="quick-view-skeleton quick-view-skeleton--matrix-status"></span><span class="quick-view-skeleton quick-view-skeleton--matrix-qty"></span><span class="quick-view-skeleton quick-view-skeleton--matrix-weight"></span></div></td>`;
          }
          // Keep the cell in its Width × Length slot. Shopify zero is a grey block only.
          if (live.confirmedOut) {
            return buildUnavailableMatrixCellHtml();
          }
          const stockStatus = live.stockStatus;
          const shopifyQty = live.shopifyQty;
          const showWeightSkeleton = live.pending;
          const invQty = shopifyQty;
          variant.inventory_quantity = shopifyQty;
          variant.shopify_inventory_quantity = shopifyQty;
          const rowAvailable = stockStatus.key === 'available' && Number(stockStatus.quantity) > 0;
          const disabled = rowAvailable ? '' : ' disabled';
          const ariaDisabled = rowAvailable ? '' : ' aria-disabled="true"';
          const invAttr = ` data-variant-inventory-qty="${invQty}" data-shopify-inventory-qty="${shopifyQty}"`;
          const maxAttr = ` max="${invQty}"`;
          const inventoryApi =
            /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
          const statusHtml = buildMatrixStatusHtml(variant, {
            ...stockStatus,
            key: stockStatus.key === 'all-in-cart' ? 'available' : stockStatus.key,
          });
          const minWeightIdleLabel = formatMatrixMinWeightIdleLabel(variant, stockStatus);
          const cellStockClass = stockStatus.key === 'available' ? 'in-stock' : 'sold-out';
          const wmsStatusAttr = showWeightSkeleton
            ? 'loading'
            : stockStatus.key === 'available'
              ? 'available'
              : 'coming-soon';

          return `
<td class="quick-view-chain-matrix__cell bulk-order-matrix__variant-cell quick-view-chain-matrix__cell--${cellStockClass}">
  <div class="quick-view-chain-matrix__variant${rowAvailable ? '' : ' quick-view-chain-matrix__variant--sold-out'}" data-details-variant-row data-variant-row data-variant-id="${variant.id}" data-source-product-id="${escapeAttr(String(variant.product_id || variant.productId || product.id || ''))}" data-variant-sku="${escapeAttr(sku)}" data-variant-available="${rowAvailable ? 'true' : 'false'}" data-wms-status="${wmsStatusAttr}" data-qvm-inventory-pending="false" data-matrix-width="${escapeAttr(width)}" data-matrix-length="${escapeAttr(length)}"${invAttr}>
    ${statusHtml}
    <div class="quick-view-qty order-sheet-details-qty quantity-control bulk-order-matrix__quantity" role="group" aria-label="Quantity for ${escapeAttr(width)} x ${escapeAttr(length)}"${ariaDisabled}>
      <button type="button" class="order-sheet-details-qty__btn quick-view-qty__btn" data-details-qty-minus aria-label="Decrease quantity"${disabled}${ariaDisabled}>−</button>
      <input type="number" class="order-sheet-details-qty__input quick-view-qty__value" id="QvmQty-${variant.id}" data-details-qty-input data-quick-view-quantity value="0" min="0" step="1"${maxAttr}${invAttr} inputmode="numeric" aria-label="Quantity for ${escapeAttr(width)} x ${escapeAttr(length)}"${disabled}${ariaDisabled}>
      <button type="button" class="order-sheet-details-qty__btn quick-view-qty__btn" data-details-qty-plus aria-label="Increase quantity"${disabled}${ariaDisabled}>+</button>
    </div>
    <div class="quick-view-chain-matrix__weight-wrap">
      ${
        sku
          ? showWeightSkeleton
            ? inventoryApi && typeof inventoryApi.getInventorySkeletonMarkup === 'function'
              ? inventoryApi.getInventorySkeletonMarkup('weight-action')
              : '<span class="aone-inventory-skeleton aone-inventory-skeleton--weight-action" aria-hidden="true"></span>'
            : `<button type="button" class="order-sheet-details-variant-table__weight-btn quick-view-chain-matrix__weight-btn bulk-order-matrix__min-weight" data-details-weight-open aria-haspopup="dialog" data-matrix-width="${escapeAttr(width)}" data-matrix-length="${escapeAttr(length)}" data-weight-idle-label="${escapeAttr(minWeightIdleLabel)}"${disabled}><span data-details-weight-label>${escapeAttr(minWeightIdleLabel)}</span></button>`
          : '<button type="button" class="order-sheet-details-variant-table__weight-btn quick-view-chain-matrix__weight-btn" disabled><span class="quick-view-chain-matrix__weight-na" aria-hidden="true">Variant Not Available</span></button>'
      }
    </div>
  </div>
</td>`;
        })
        .join('');

      return `<tr><th scope="row" class="quick-view-chain-matrix__width">${escapeAttr(width)}</th>${cells}</tr>`;
    })
    .join('');

  // Weight/WMS notice only — stock status already comes from Shopify.
  const inventoryNotice = wmsLoading
    ? `<p class="quick-view-size-selector__inventory-note" role="status" aria-live="polite"><span class="visually-hidden">Loading weight details</span></p>`
    : wmsError
      ? `<div class="quick-view-size-selector__inventory-error" role="alert"><span>Unable to load weight options.</span><button type="button" class="quick-view-size-selector__retry-btn" data-qvm-wms-retry>Retry</button></div>`
      : '';

  return `
<div class="quick-view-size-selector quick-view-size-selector--chain chain-bulk-order-matrix" data-size-selector aria-busy="${wmsLoading ? 'true' : 'false'}">
  <div class="quick-view-size-selector__header order-sheet-details-chain-matrix__section-header bulk-matrix-section-header">
    <div class="bulk-matrix-section-header__main">
      <h2 class="quick-view-size-selector__matrix-title bulk-matrix-title">Bulk Ordering Matrix</h2>
      <span class="bulk-matrix-variant-count">${escapeAttr(variantCountLabel)}</span>
    </div>
  </div>
  ${inventoryNotice}
  <div class="quick-view-chain-matrix__scroll" aria-live="polite">
    <table
      class="quick-view-chain-matrix${compactClass}"
      aria-label="Chain bulk ordering matrix"
      data-matrix-length-count="${lengthCount}"
      style="--matrix-length-count: ${lengthCount}"
    >
      <colgroup>
        <col class="quick-view-chain-matrix__col-axis">
        <col class="quick-view-chain-matrix__col-length" span="${lengthCount}">
      </colgroup>
      <thead>
        <tr>
          <th scope="col" rowspan="2" class="quick-view-chain-matrix__corner quick-view-chain-matrix__corner-row1"><span class="quick-view-chain-matrix__corner-label">WIDTH (MM)</span></th>
          <th scope="col" colspan="${lengthCount}" class="quick-view-chain-matrix__length-group">Length (inches)</th>
        </tr>
        <tr>
          ${headCells}
        </tr>
      </thead>
      <tbody class="quick-view-chain-matrix__body">${bodyRows}</tbody>
    </table>
  </div>
</div>`;
}

/**
 * @param {string} productId
 * @returns {string}
 */
function buildAddToCartHtml(productId) {
  return `
<div class="quick-view-add-to-cart" data-quick-view-cta>
  <div class="quick-view-add-to-cart__summary" data-qvm-summary aria-live="polite">
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Selected Variants:</span>
      <span class="quick-view-add-to-cart__stat-value" data-qvm-selected-count data-qvm-selected-count-value>0</span>
    </div>
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Total Quantity:</span>
      <span class="quick-view-add-to-cart__stat-value" data-qvm-total-qty>0</span>
    </div>
    <div class="quick-view-add-to-cart__stat">
      <span class="quick-view-add-to-cart__stat-label">Total Weight:</span>
      <span class="quick-view-add-to-cart__stat-value" data-qvm-total-weight>0.00g</span>
    </div>
  </div>
  <div class="quick-view-add-to-cart__actions">
    <button type="button" class="quick-view-add-to-cart__button" data-add-selected data-product-id="${escapeAttr(productId)}">
      <svg class="quick-view-add-to-cart__cart-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 6h15l-1.5 9h-12L6 6z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
        <path d="M6 6l-1-3H2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        <circle cx="9.5" cy="19.5" r="1.25" fill="currentColor"/>
        <circle cx="17.5" cy="19.5" r="1.25" fill="currentColor"/>
      </svg>
      <span data-add-selected-label>Add Selected To Cart</span>
    </button>
  </div>
</div>`;
}

/** @param {HTMLInputElement} input */
function readRowQty(input) {
  const v = parseInt(String(input.value), 10);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * Sum numeric grams from a comma-separated weight string when possible.
 * @param {string} weightStr
 * @returns {number}
 */
function parseWeightGrams(weightStr) {
  if (!weightStr) return 0;
  return weightStr
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .reduce((sum, part) => {
      const match = part.match(/([\d.]+)/);
      if (!match) return sum;
      const n = parseFloat(match[1]);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
}

class QuickViewModalController {
  /** @type {HTMLElement | null} */
  #root = null;
  /** @type {AbortController | null} */
  #fetchAbort = null;
  /** @type {number} */
  #activeRequestId = 0;
  /** @type {string} */
  #activeProductHandle = '';
  /** @type {object | null} */
  #product = null;
  /** @type {Map<string, Record<string, any>>} */
  #productCacheByHandle = new Map();
  /** @type {(e: KeyboardEvent) => void} */
  #onKey = this.#handleKeydown.bind(this);
  /** @type {string} */
  #prevBodyOverflow = '';
  /** @type {string} */
  #prevHtmlOverflow = '';
  /** @type {HTMLDialogElement | null} */
  #suspendedDialog = null;
  /** @type {boolean} */
  #isSubmittingCart = false;
  /** @type {HTMLElement | null} */
  #matrixInfoTooltip = null;
  /** @type {HTMLButtonElement | null} */
  #matrixInfoActiveBtn = null;
  /** @type {number} */
  #matrixInfoShownAt = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #matrixInfoHideTimer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #matrixInfoCopyResetTimer = null;
  /** @type {((detail: any) => void) | null} */
  #cartInventoryUnsub = null;
  /** @type {((event: Event) => void) | null} */
  #cartInventoryListener = null;
  /** @type {(() => void) | null} */
  #productAvailabilityUnsub = null;

  constructor() {
    this.#root = document.querySelector(SEL.root);
    if (!this.#root) return;
    // Window capture runs before document capture, so this runs before the theme's
    // delegated `on:click` handlers (see component.js). Stopping here prevents the
    // legacy quick-add `<dialog>` from opening on top of this modal.
    window.addEventListener('click', this.#onPlpAtcClickCapture, true);
    this.#root.addEventListener('click', this.#onRootClick.bind(this));
    this.#bindMatrixInfoTooltips();
    const detailsRoot = this.#root.querySelector(SEL.detailsRoot);
    if (detailsRoot instanceof HTMLElement) {
      const onQtyUiChange = () => {
        this.#updateCtaDisabled();
        this.#syncSelectionUi();
      };
      detailsRoot.addEventListener('plp-details-qty-update', onQtyUiChange);
      detailsRoot.addEventListener('input', (e) => {
        if (
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-details-qty-input]')
        ) {
          onQtyUiChange();
        }
      });
      detailsRoot.addEventListener('change', (e) => {
        if (
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-details-qty-input]')
        ) {
          onQtyUiChange();
        }
      });
      detailsRoot.addEventListener('click', (e) => {
        const t = e.target;
        const el = t instanceof Element ? t : t instanceof Node ? t.parentElement : null;
        if (el?.closest('[data-details-qty-minus], [data-details-qty-plus]')) {
          // Run after plp-table-details mutates the canonical qty input.
          queueMicrotask(onQtyUiChange);
        }
      });
      detailsRoot.addEventListener('input', (e) => {
        if (
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-qvm-qty-display-input]')
        ) {
          this.#handleModalOnlyQtyDisplayInput(e.target, false);
        }
      });
      detailsRoot.addEventListener('change', (e) => {
        if (
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-qvm-qty-display-input]')
        ) {
          this.#handleModalOnlyQtyDisplayInput(e.target, true);
        }
      });
      detailsRoot.addEventListener(
        'blur',
        (e) => {
          if (
            e.target instanceof HTMLInputElement &&
            e.target.matches('[data-qvm-qty-display-input]')
          ) {
            this.#handleModalOnlyQtyDisplayInput(e.target, true);
          }
        },
        true
      );
      detailsRoot.addEventListener('keydown', (e) => {
        if (
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-qvm-qty-display-input]') &&
          e.key === 'Enter'
        ) {
          e.preventDefault();
          this.#handleModalOnlyQtyDisplayInput(e.target, true);
          e.target.blur();
        }
      });
      detailsRoot.addEventListener(
        'wheel',
        (e) => {
          if (
            e.target instanceof HTMLInputElement &&
            e.target.matches('[data-qvm-qty-display-input], [data-details-qty-input]')
          ) {
            e.preventDefault();
          }
        },
        { passive: false }
      );
    }
    this.#ensurePanelBound();
  }

  /** Bind weight-modal handlers once plp-table-details.js has loaded. */
  #ensurePanelBound() {
    const panel = this.#root?.querySelector(SEL.detailsPanel);
    if (!(panel instanceof HTMLElement)) return;
    if (panel.dataset.plpDetailsBound === 'true') return;
    window.plpVariantDetails?.bindPanel?.(panel);
  }

  /** @param {MouseEvent} e */
  #onPlpAtcClickCapture = (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    const btn = t.closest(
      '.plp-card .plp-card__atc, .inventory-product-row[data-product-handle] [data-add-to-cart-choose], .inventory-product-row[data-product-handle] .plp-card__atc, .image-search__product-card[data-product-handle] .plp-card__atc'
    );
    if (!btn) return;
    if (btn instanceof HTMLButtonElement && btn.disabled) return;
    const card =
      btn.closest('.plp-card') ||
      btn.closest('.inventory-product-row[data-product-handle]') ||
      btn.closest('.image-search__product-card[data-product-handle]');
    if (!card) return;
    // Do not gate Quick View on Shopify/Wizzy data-product-available.
    // WMS inside the modal is the storefront availability source of truth.
    let handle = card.getAttribute('data-product-handle') || '';
    if (!handle) {
      const link = card.querySelector('a[href*="/products/"]');
      const href = link instanceof HTMLAnchorElement ? link.getAttribute('href') || '' : '';
      const match = href.match(/\/products\/([^/?#]+)/);
      if (match && match[1]) {
        handle = decodeURIComponent(match[1]);
        card.setAttribute('data-product-handle', handle);
      }
    }
    if (!handle) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    const payload = resolveCallerProductPayload(card, handle);
    this.open({ handle, product: payload });
  };

  /**
    * Demote the image search <dialog> from the browser's top layer (showModal)
   * to a regular open dialog (.show) so it stays visible behind this modal.
   * The quick-view's z-index (60) is higher than the dialog's (20), so it
   * layers correctly once the dialog leaves the top layer.
   */
  #suspendImageSearchDialog() {
    const dlg = /** @type {HTMLDialogElement | null} */ (
      document.querySelector('[data-image-search-dialog]')
    );
    if (dlg && dlg.open) {
      this.#suspendedDialog = dlg;
      // Flag before close() so image-search's close handler skips cleanup
      dlg.dataset.suspended = 'true';
      dlg.close();
      dlg.show();
    }
  }

  #restoreImageSearchDialog() {
    const dlg = this.#suspendedDialog;
    if (dlg) {
      this.#suspendedDialog = null;
      if (dlg.open) dlg.close();
      // delete dlg.dataset.suspended;
      try {
        dlg.showModal();
      } catch (_) {}

      requestAnimationFrame(() => {
        delete dlg.dataset.suspended;
      });
    }
  }

  /** Closes the theme's default quick-add dialog if it was opened (empty white box). */
  #closeLegacyQuickAddDialog() {
    const legacy = document.getElementById('quick-add-dialog');
    if (legacy && typeof /** @type {{ closeDialog?: () => void }} */ (legacy).closeDialog === 'function') {
      legacy.closeDialog();
    }
  }

  /** Clear all product-scoped quick-view state so stale data never flashes. */
  #resetQuickViewState() {
    if (!this.#root) return;

    this.#hideMatrixInfoTooltip();
    this.#root.removeAttribute('aria-busy');
    this.#root.classList.remove('quick-modal--chain', 'quick-modal--chain-no-media');

    const mImg = this.#root.querySelector(SEL.mountImage);
    const mInfo = this.#root.querySelector(SEL.mountInfo);
    const mSizes = this.#root.querySelector(SEL.mountSizes);
    const mCta = this.#root.querySelector(SEL.mountCta);
    const detailsRoot = this.#root.querySelector(SEL.detailsRoot);

    this.#product = null;
    if (detailsRoot instanceof HTMLElement) {
      delete detailsRoot.dataset.productId;
      delete detailsRoot.dataset.productHandle;
      delete detailsRoot.dataset.isProductReady;
      delete detailsRoot.dataset.isInventoryLoading;
      delete detailsRoot.dataset.inventoryError;
      delete detailsRoot.dataset.wmsDetailsLoading;
      delete detailsRoot.dataset.wmsDetailsError;
    }

    if (mImg) mImg.innerHTML = '';
    if (mInfo) mInfo.innerHTML = '';
    if (mSizes) mSizes.innerHTML = '';
    if (mCta) mCta.innerHTML = '';

    const weightModal =
      (detailsRoot instanceof HTMLElement && detailsRoot.querySelector('[data-order-sheet-weight-modal]')) ||
      this.#root.querySelector('[data-quick-view-weight-modal]');
    if (!(weightModal instanceof HTMLElement)) return;

    weightModal.hidden = true;
    document.body.classList.remove('order-sheet-weight-modal-open');

    const status = weightModal.querySelector('[data-order-sheet-weight-status]');
    const optionsWrap = weightModal.querySelector('[data-order-sheet-weight-options-wrap]');
    const options = weightModal.querySelector('[data-order-sheet-weight-options]');
    const empty = weightModal.querySelector('[data-order-sheet-weight-empty]');
    const error = weightModal.querySelector('[data-order-sheet-weight-error]');
    const loading = weightModal.querySelector('[data-order-sheet-weight-loading]');
    const summary = weightModal.querySelector('[data-order-sheet-weight-summary]');
    const summaryQty = weightModal.querySelector('[data-order-sheet-weight-summary-qty]');
    const summaryTotal = weightModal.querySelector('[data-order-sheet-weight-summary-total]');
    const confirm = weightModal.querySelector('[data-order-sheet-weight-confirm]');

    if (status) status.textContent = '';
    if (optionsWrap instanceof HTMLElement) optionsWrap.hidden = true;
    if (options) options.innerHTML = '';
    if (empty instanceof HTMLElement) empty.hidden = true;
    if (error instanceof HTMLElement) error.hidden = true;
    if (loading instanceof HTMLElement) loading.hidden = true;
    if (summary) summary.textContent = '0';
    if (summaryQty) summaryQty.textContent = '0';
    if (summaryTotal) summaryTotal.textContent = '0.00g';
    if (confirm instanceof HTMLButtonElement) confirm.disabled = true;
  }

  /**
   * Paint the skeleton that matches the final layout.
   * Matrix only when `isMatrixProduct` is already true. Otherwise table, or a neutral block.
   * @param {object | null | undefined} [product]
   */
  #showLoadingSkeleton(product) {
    if (!this.#root) return;
    const mImg = this.#root.querySelector(SEL.mountImage);
    const mInfo = this.#root.querySelector(SEL.mountInfo);
    const mSizes = this.#root.querySelector(SEL.mountSizes);
    const mCta = this.#root.querySelector(SEL.mountCta);
    const layout = resolveQuickViewSkeletonLayout(product);
    const skeleton = buildQuickViewLoadingSkeleton(layout);
    if (mImg) mImg.innerHTML = skeleton.image;
    if (mInfo) mInfo.innerHTML = skeleton.info;
    if (mSizes) mSizes.innerHTML = skeleton.sizes;
    if (mCta) mCta.innerHTML = skeleton.cta;
    // Chain shell sizing belongs to the matrix skeleton only. hydrate() sets the real mode.
    this.#root.classList.toggle('quick-modal--chain', layout === 'matrix');
    this.#root.setAttribute('aria-busy', 'true');
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} signal
   * @returns {Promise<Record<string, any>>}
   */
  async #fetchProductByHandle(handle, signal) {
    const cacheKey = String(handle || '').trim().toLowerCase();
    if (!cacheKey) throw new Error('Missing product handle');
    const cached = this.#productCacheByHandle.get(cacheKey);
    if (cached) return normalizeProductPayload(cached, handle) || cached;

    const productUrl = `/products/${encodeURIComponent(handle)}.js`;
    const productRes = await fetch(productUrl, { signal, headers: { Accept: 'application/json' } });
    if (!productRes.ok) throw new Error(`Product fetch failed: ${productRes.status}`);
    const fetchedProduct = await productRes.json();
    const normalized = normalizeProductPayload(fetchedProduct, handle) || fetchedProduct;
    this.#productCacheByHandle.set(cacheKey, normalized);
    return normalized;
  }

  /**
   * @param {Record<string, any>} product
   * @returns {boolean}
   */
  #productHasResolvableInventory(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.some((variant) => normalizeSku(variant?.sku != null ? String(variant.sku) : '') !== '');
  }

  /**
   * @param {string | { handle?: string, product?: Record<string, any> | null }} input
   */
  async open(input) {
    if (!this.#root) return;
    const openArg = typeof input === 'string' ? { handle: input, product: null } : input || {};
    const callerProduct =
      openArg.product && typeof openArg.product === 'object'
        ? normalizeProductPayload(openArg.product, String(openArg.handle || ''))
        : null;
    const safeHandle = String(openArg.handle || callerProduct?.handle || '').trim();
    if (!safeHandle) return;

    const requestId = ++this.#activeRequestId;
    this.#activeProductHandle = safeHandle;
    this.#fetchAbort?.abort();
    const controller = new AbortController();
    this.#fetchAbort = controller;

    // Clear previous product immediately so stale WMS values never flash.
    this.#unbindProductAvailabilitySubscription();
    this.#resetQuickViewState();
    // Fill mounts before the dialog becomes visible — otherwise users see a blank sheet
    // while cart sync /product JSON fetch runs.
    // Layout comes from the card payload, data-product-type, or the collection page.
    // All three are inputs to isMatrixProduct. A card with no type must not hide the collection signal.
    const layoutProduct = productHasLayoutSignal(callerProduct)
      ? callerProduct
      : lookupLayoutProductByHandle(safeHandle);
    this.#showLoadingSkeleton(layoutProduct);
    this.#bindCartInventorySubscription();

    this.#closeLegacyQuickAddDialog();
    this.#suspendImageSearchDialog();
    this.#root.classList.add('quick-modal--loading');
    this.#root.removeAttribute('hidden');
    this.#root.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      this.#root?.classList.add('quick-modal--open');
    });
    this.#prevBodyOverflow = document.body.style.overflow;
    this.#prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.classList.add('quick-modal-open');
    document.documentElement.classList.add('quick-modal-open');
    document.addEventListener('keydown', this.#onKey);

    try {
      const signal = controller.signal;
      const inventoryApi =
        /** @type {any} */ (window).AOneWmsAvailability || /** @type {any} */ (window).WMSInventory;

      // Await cart before first inventory paint so reserved serials are subtracted.
      if (inventoryApi && typeof inventoryApi.ensureCartSerialsSynced === 'function') {
        try {
          await inventoryApi.ensureCartSerialsSynced(true);
        } catch (_cartError) {
          /* keep skeleton path via isCartStateReady checks */
        }
      }
      if (
        signal.aborted ||
        requestId !== this.#activeRequestId ||
        this.#activeProductHandle !== safeHandle
      ) {
        return;
      }

      const product = await attachLinkedFamily(
        callerProduct || (await this.#fetchProductByHandle(safeHandle, signal)),
        safeHandle,
        signal
      );
      if (
        signal.aborted ||
        requestId !== this.#activeRequestId ||
        this.#activeProductHandle !== safeHandle
      ) {
        return;
      }

      this.#product = product;
      if (safeHandle) {
        this.#productCacheByHandle.set(safeHandle.toLowerCase(), product);
      }

      // Shopify inventory for IN-STOCK / COMING SOON (independent of WMS weight fetch).
      try {
        const handles =
          /** @type {any} */ (window).AoneLinkedProducts?.collectHandles?.(product) || [safeHandle];
        const maps = await Promise.all(
          (Array.isArray(handles) && handles.length ? handles : [safeHandle]).map((handle) =>
            fetchVariantStockMap(String(handle), signal)
          )
        );
        const stockMap = Object.assign({}, ...maps);
        applyStockMapToProduct(product, stockMap);
      } catch (stockError) {
        if (stockError && /** @type {Error} */ (stockError).name === 'AbortError') return;
        console.warn('[quick-view-modal] Shopify stock map unavailable', stockError);
      }
      if (
        signal.aborted ||
        requestId !== this.#activeRequestId ||
        this.#activeProductHandle !== safeHandle
      ) {
        return;
      }

      const wmsReady = isWmsReadyForProduct(product);
      if (wmsReady) {
        applyBatchDataToProduct(product);
      }

      const needsInventory = this.#productHasResolvableInventory(product);
      const cartReady =
        !inventoryApi ||
        typeof inventoryApi.isCartStateReady !== 'function' ||
        inventoryApi.isCartStateReady() === true;

      // Stock paints from Shopify immediately; weight cells stay pending until WMS ready.
      this.#hydrate(product, {
        wmsLoading: (!wmsReady || !cartReady) && needsInventory,
      });
      this.#root.classList.remove('quick-modal--loading');
      this.#root.removeAttribute('aria-busy');

      // React to products-batch completion for THIS product while the modal stays open.
      // Collection/SLP upsertBatchProducts notifies here — without this, skeleton never clears.
      if (needsInventory) {
        this.#bindProductAvailabilitySubscription(familyProductIds(product));
      }

      const dialog = this.#root.querySelector(SEL.dialog);
      if (dialog instanceof HTMLElement) {
        dialog.focus();
      }

      if (!needsInventory) return;

      if (!wmsReady) {
        const wmsResult = await prefetchWeightsForQuickView(product);
        if (
          signal.aborted ||
          requestId !== this.#activeRequestId ||
          this.#activeProductHandle !== safeHandle
        ) {
          return;
        }

        const readyAfterPrefetch = isWmsReadyForProduct(product);
        if (!wmsResult.ok || !readyAfterPrefetch) {
          console.error('[quick-view-modal] WMS details unavailable', wmsResult.error || 'cache miss');
          this.#hydrate(product, { wmsError: true, matrixOnly: true });
          return;
        }

        applyBatchDataToProduct(product);
      }

      if (
        signal.aborted ||
        requestId !== this.#activeRequestId ||
        this.#activeProductHandle !== safeHandle
      ) {
        return;
      }

      this.#logInventoryDebug(product, 'hydrate');
      this.#hydrate(product, { matrixOnly: true });
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('[quick-view-modal]', err);
      this.close();
      return;
    } finally {
      if (requestId === this.#activeRequestId) {
        this.#root?.classList.remove('quick-modal--loading');
        this.#root?.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {object} product
   * @param {{ wmsLoading?: boolean, wmsError?: boolean, matrixOnly?: boolean }} [options]
   */
  #hydrate(product, options = {}) {
    const mImg = this.#root?.querySelector(SEL.mountImage);
    const mInfo = this.#root?.querySelector(SEL.mountInfo);
    const mSizes = this.#root?.querySelector(SEL.mountSizes);
    const mCta = this.#root?.querySelector(SEL.mountCta);
    const detailsRoot = this.#root?.querySelector(SEL.detailsRoot);
    if (!mImg || !mInfo || !mSizes || !mCta || !detailsRoot) return;

    const matrixOnly = options.matrixOnly === true;
    const wmsLoading = options.wmsLoading === true;
    const wmsError = options.wmsError === true;
    const placeholderImage = getPlaceholderProductImage(this.#root);

    if (!matrixOnly) {
      const isChain = isMatrixProduct(product);
      const hasMedia = getProductImageUrls(product).length > 0;
      this.#root?.classList.toggle('quick-modal--chain', isChain);
      this.#root?.classList.toggle('quick-modal--chain-no-media', isChain && !hasMedia);
      mImg.innerHTML = buildImageHtml(product, placeholderImage, { isChain });
      mInfo.innerHTML = buildProductInfoHtml(product);
      mCta.innerHTML = buildAddToCartHtml(String(product.id));

      const weightModal =
        detailsRoot.querySelector('[data-order-sheet-weight-modal]') ||
        this.#root?.querySelector('[data-quick-view-weight-modal]') ||
        document.querySelector('#QuickViewModal [data-quick-view-weight-modal]');
      if (weightModal instanceof HTMLElement) {
        weightModal.hidden = true;
        document.body.classList.remove('order-sheet-weight-modal-open');
        const vendorEl = weightModal.querySelector('.order-sheet-weight-modal__product-vendor');
        if (vendorEl) {
          vendorEl.textContent = String(product.vendor || 'A-ONE GOLD & DIAMONDS LLC').toUpperCase();
        }
        const titleEl = weightModal.querySelector('#quick-view-weight-modal-title');
        if (titleEl) titleEl.textContent = String(product.title || 'Select Weight');
        const metaEl = weightModal.querySelector('.order-sheet-weight-modal__product-meta');
        if (metaEl instanceof HTMLElement) {
          metaEl.hidden = true;
          const widthValue = weightModal.querySelector('[data-modal-width]');
          const lengthValue = weightModal.querySelector('[data-modal-length]');
          if (widthValue instanceof HTMLElement) widthValue.textContent = '';
          if (lengthValue instanceof HTMLElement) lengthValue.textContent = '';
        }
        const modalImage = weightModal.querySelector('.order-sheet-weight-modal__product-image');
        if (modalImage instanceof HTMLImageElement) {
          const imgSrc = getProductImageUrls(product)[0] || '';
          if (imgSrc) {
            modalImage.src = imgSrc;
            modalImage.alt = String(product.title || '');
            if (placeholderImage) {
              modalImage.onerror = () => {
                modalImage.onerror = null;
                modalImage.src = placeholderImage;
                modalImage.alt = String(product.title || '');
                modalImage.classList.add('order-sheet-weight-modal__product-image--placeholder');
              };
            }
            modalImage.classList.remove('order-sheet-weight-modal__product-image--placeholder');
          } else {
            modalImage.src = placeholderImage || 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
            modalImage.alt = String(product.title || '');
            modalImage.onerror = null;
            modalImage.classList.add('order-sheet-weight-modal__product-image--placeholder');
          }
        }
      }
    }

    mSizes.innerHTML = buildSizeSelectorHtml(product, { wmsLoading, wmsError });

    const matrixScroll = this.#root?.querySelector('.quick-view-chain-matrix__scroll');
    if (matrixScroll instanceof HTMLElement) {
      requestAnimationFrame(() => bindQuickViewMatrixStickyHeaderSync(matrixScroll));
    }

    detailsRoot.dataset.productId = String(product.id);
    detailsRoot.dataset.productHandle = String(product.handle || '');
    detailsRoot.dataset.isChainProduct = isMatrixProduct(product) ? 'true' : 'false';
    detailsRoot.dataset.productType = String(product.product_type || product.type || '');
    detailsRoot.dataset.isProductReady = 'true';
    detailsRoot.dataset.isInventoryLoading = wmsLoading ? 'true' : 'false';
    detailsRoot.dataset.inventoryError = wmsError ? 'true' : 'false';
    detailsRoot.dataset.wmsDetailsLoading = wmsLoading ? 'true' : 'false';
    detailsRoot.dataset.wmsDetailsError = wmsError ? 'true' : 'false';
    this.#syncProductWmsBadge(product, { wmsLoading, wmsError });

    let productJsonScript = detailsRoot.querySelector('[data-plp-product-json]');
    if (!(productJsonScript instanceof HTMLScriptElement)) {
      productJsonScript = document.createElement('script');
      productJsonScript.type = 'application/json';
      productJsonScript.dataset.plpProductJson = '';
      detailsRoot.appendChild(productJsonScript);
    }
    productJsonScript.textContent = JSON.stringify(product);

    this.#ensurePanelBound();
    this.#initQtyRowStates(detailsRoot);
    this.#updateCtaDisabled();
    this.#syncSelectionUi();
  }

  /**
   * Product-info badge from Shopify inventory totals (IN STOCK / COMING SOON).
   * @param {object} product
   * @param {{ wmsLoading?: boolean, wmsError?: boolean }} [options]
   */
  #syncProductWmsBadge(product, options = {}) {
    const badge = this.#root?.querySelector('[data-qvm-wms-badge]');
    if (!(badge instanceof HTMLElement)) return;

    // Stock badge is Shopify-owned — do not skeleton-wait on WMS.
    badge.removeAttribute('aria-busy');

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    let totalShopifyQty = 0;
    variants.forEach((variant) => {
      totalShopifyQty += getShopifyVariantInventoryQty(variant);
    });

    if (totalShopifyQty > 0) {
      badge.textContent = 'AVAILABLE';
      badge.classList.add('quick-view-product-info__badge--in-stock');
      badge.classList.remove('quick-view-product-info__badge--out-of-stock');
    } else {
      badge.textContent = 'COMING SOON';
      badge.classList.add('quick-view-product-info__badge--out-of-stock');
      badge.classList.remove('quick-view-product-info__badge--in-stock');
    }
  }

  /** Retry only the inventory enrichment for the current product. */
  async #retryWmsDetails() {
    const product = this.#product;
    if (!product || !this.#root) return;
    if (!this.#productHasResolvableInventory(product)) return;

    const requestId = this.#activeRequestId;
    const handle = this.#activeProductHandle;
    this.#hydrate(product, { wmsLoading: true, matrixOnly: true });

    try {
      const productId = normalizeShopifyId(product.id);
      const api = /** @type {any} */ (window).AOneProductWeightsAPI || /** @type {any} */ (window).AoneBatchWeight;
      if (api && typeof api.loadCurrentProductWeights === 'function' && productId) {
        await api.loadCurrentProductWeights([productId]);
      } else {
        await prefetchWeightsForQuickView(product);
      }

      if (requestId !== this.#activeRequestId || this.#activeProductHandle !== handle) return;

      const ready = isWmsReadyForProduct(product);
      const store = /** @type {any} */ (window).AOneProductWeights;
      if (!ready || store?.status === 'error') {
        console.error('[quick-view-modal] WMS retry failed', store.lastError);
        this.#hydrate(product, { wmsError: true, matrixOnly: true });
        return;
      }

      applyBatchDataToProduct(product);
      this.#hydrate(product, { matrixOnly: true });
    } catch (err) {
      if (err && /** @type {Error} */ (err).name === 'AbortError') return;
      console.error('[quick-view-modal] WMS retry', err);
      if (requestId !== this.#activeRequestId || this.#activeProductHandle !== handle) return;
      this.#hydrate(product, { wmsError: true, matrixOnly: true });
    }
  }

  /** Sync minus/plus disabled state after table HTML is replaced. */
  /** @param {HTMLElement} detailsRoot */
  #initQtyRowStates(detailsRoot) {
    // Paint cart baseline first so qty max / controls account for in-cart units.
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      /** @type {any} */ (window).plpVariantDetails?.applyCartSelectionToRow?.(row);
    });

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const modalOnly = row.querySelector('[data-details-qty-modal-only]');
      const input = row.querySelector('[data-details-qty-input]');
      const minus = row.querySelector('[data-details-qty-minus]');
      const plus = row.querySelector('[data-details-qty-plus]');
      const available = row.dataset.variantAvailable !== 'false';
      const inventoryPending = row.dataset.qvmInventoryPending === 'true';
      const shopifyAttr = row.getAttribute('data-shopify-inventory-qty');
      const shopifyQty =
        shopifyAttr != null && shopifyAttr !== '' && Number.isFinite(Number(shopifyAttr))
          ? Math.max(0, Math.floor(Number(shopifyAttr)))
          : null;
      const shopifyInStock = shopifyQty != null && shopifyQty > 0;
      const selectableMax = this.#getRowSelectableMax(row, input instanceof HTMLInputElement ? input : null);
      const selectableMaxNum = Number.isFinite(selectableMax) ? selectableMax : null;
      const cartBaseline = Number(row.dataset.cartBaselineQty || 0);
      const cartBaselineNum =
        Number.isFinite(cartBaseline) && cartBaseline > 0 ? Math.floor(cartBaseline) : 0;
      const orderMaxNum = shopifyInStock ? shopifyQty : selectableMaxNum;
      const absoluteMax = orderMaxNum == null ? null : orderMaxNum + cartBaselineNum;
      // Shopify qty keeps the stepper usable while weight data is still loading.
      const effectiveRowInteractive =
        (shopifyInStock && !inventoryPending) ||
        (available && !inventoryPending && (selectableMaxNum == null ? true : selectableMaxNum > 0)) ||
        cartBaselineNum > 0;

      if (modalOnly && input instanceof HTMLInputElement) {
        // Hidden modal-only input must be enabled when inventory is available —
        // otherwise plus handlers bail on input.disabled despite visible controls being on.
        input.disabled = !effectiveRowInteractive;
        if (effectiveRowInteractive) {
          input.removeAttribute('aria-disabled');
          input.removeAttribute('disabled');
        } else {
          input.setAttribute('aria-disabled', 'true');
        }
        if (orderMaxNum != null) input.max = String(absoluteMax != null ? absoluteMax : orderMaxNum);

        const displayInput = row.querySelector('[data-qvm-qty-display-input]');
        const modalMinus = row.querySelector('[data-qvm-qty-minus]');
        const modalPlus = row.querySelector('[data-qvm-qty-plus]');
        if (displayInput instanceof HTMLInputElement) {
          displayInput.value = input.value;
          displayInput.disabled = !effectiveRowInteractive;
          if (effectiveRowInteractive) displayInput.removeAttribute('aria-disabled');
          else displayInput.setAttribute('aria-disabled', 'true');
          if (orderMaxNum != null) displayInput.max = String(absoluteMax != null ? absoluteMax : orderMaxNum);
        }
        const qty = readRowQty(input);
        if (modalMinus instanceof HTMLButtonElement) {
          modalMinus.disabled = !effectiveRowInteractive || qty <= 0;
          if (effectiveRowInteractive) modalMinus.removeAttribute('aria-disabled');
        }
        if (modalPlus instanceof HTMLButtonElement) {
          modalPlus.disabled =
            !effectiveRowInteractive || (absoluteMax != null && qty >= absoluteMax);
          if (effectiveRowInteractive) modalPlus.removeAttribute('aria-disabled');
        }
        const weightBtn = row.querySelector('[data-details-weight-open]');
        if (weightBtn instanceof HTMLButtonElement) {
          const weightsStillLoading = row.dataset.wmsStatus === 'loading';
          weightBtn.disabled = !effectiveRowInteractive || weightsStillLoading;
          if (effectiveRowInteractive && !weightsStillLoading) weightBtn.removeAttribute('aria-disabled');
          else weightBtn.setAttribute('aria-disabled', 'true');
        }
        const weightLabel = row.querySelector('[data-details-weight-label]');
        if (weightLabel instanceof HTMLElement && !inventoryPending) {
          const inventoryApi =
            /** @type {any} */ (window).WMSInventory || /** @type {any} */ (window).AOneWmsAvailability;
          const selected = String(row.dataset.serialNumbersValue || '').trim();
          const sku = String(row.dataset.variantSku || '').trim();

          if (!effectiveRowInteractive) {
            if (cartBaselineNum <= 0 && !shopifyInStock) {
              delete row.dataset.weightValue;
              delete row.dataset.serialNumbersValue;
              row.classList.remove('has-weight-selected');
              weightLabel.textContent = 'Coming Soon';
              const stockEl = row.querySelector('.quick-view-stock-qty');
              if (stockEl instanceof HTMLElement) stockEl.textContent = '0';
              row.dataset.variantInventoryQty = '0';
            }
            return;
          }

          if (selected) {
            weightLabel.textContent = 'Edit weight';
            return;
          }

          const fromApi = /** @type {any} */ (window).plpVariantDetails?.getSelectableWeightEntriesForSku;
          if (inventoryApi && typeof fromApi === 'function' && sku) {
            const detailsRoot = row.closest('[data-plp-expanded-details], [data-quick-view-details]');
            /** @type {Set<string>} */
            const exclude = new Set();
            if (detailsRoot instanceof HTMLElement) {
              detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((other) => {
                if (!(other instanceof HTMLElement) || other === row) return;
                String(other.dataset.serialNumbersValue || '')
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .forEach((serial) => exclude.add(serial));
              });
            }

            const entries = fromApi(sku, exclude) || [];
            const minEntry = entries.length ? entries[0] : null;
            const minWeight = minEntry
              ? Number.isFinite(Number(minEntry.weightGrams))
                ? Number(minEntry.weightGrams)
                : parseWeightGrams(String(minEntry.label || '')) || null
              : null;
            const unit = minEntry && minEntry.unit ? String(minEntry.unit) : 'g';
            const inventoryMax = Number(row.dataset.variantInventoryQty);
            const effectiveQty = Number.isFinite(inventoryMax)
              ? Math.min(Math.max(0, inventoryMax), entries.length)
              : entries.length;

            const state =
              typeof inventoryApi.resolveWeightButtonState === 'function'
                ? inventoryApi.resolveWeightButtonState({
                    isLoading: false,
                    isAvailable: true,
                    effectiveAvailableQuantity: effectiveQty,
                    minimumWeight: minWeight,
                    selectedSerialNumbers: '',
                    unit,
                  })
                : {
                    label: entries.length
                      ? Number.isFinite(Number(minWeight)) && Number(minWeight) > 0
                        ? inventoryApi.formatMinimumWeightLabel
                          ? inventoryApi.formatMinimumWeightLabel(minWeight, { unit })
                          : `Min wt: ${String(minWeight).replace(/\.?0+$/, '')}${unit}`
                        : 'Select Weight'
                      : 'Coming Soon',
                    disabled: entries.length <= 0,
                  };

            weightLabel.textContent =
              shopifyInStock && /^coming soon$/i.test(String(state.label || '').trim())
                ? 'Select Weight'
                : state.label || (shopifyInStock || effectiveQty > 0 ? 'Select Weight' : 'Coming Soon');
            if (weightBtn instanceof HTMLButtonElement) {
              weightBtn.disabled = Boolean(state.disabled);
              weightBtn.setAttribute('aria-disabled', state.disabled ? 'true' : 'false');
            }
          } else {
            weightLabel.textContent = 'Select Weight';
          }
        }
        return;
      }

      if (!(input instanceof HTMLInputElement) || !(minus instanceof HTMLButtonElement)) return;
      const qty = readRowQty(input);
      input.disabled = !effectiveRowInteractive;
      if (effectiveRowInteractive) {
        input.removeAttribute('aria-disabled');
        input.removeAttribute('disabled');
      }
      if (absoluteMax != null) input.max = String(absoluteMax);
      minus.disabled = !effectiveRowInteractive || qty <= 0;
      if (effectiveRowInteractive) minus.removeAttribute('aria-disabled');
      if (plus instanceof HTMLButtonElement) {
        plus.disabled = !effectiveRowInteractive || (absoluteMax != null && qty >= absoluteMax);
        if (effectiveRowInteractive) plus.removeAttribute('aria-disabled');
      }
      const weightBtn = row.querySelector('[data-details-weight-open]');
      if (weightBtn instanceof HTMLButtonElement) {
        weightBtn.disabled = !effectiveRowInteractive;
        if (effectiveRowInteractive) weightBtn.removeAttribute('aria-disabled');
      }

      if (!inventoryPending && !effectiveRowInteractive && cartBaselineNum <= 0 && !shopifyInStock) {
        delete row.dataset.weightValue;
        delete row.dataset.serialNumbersValue;
        row.classList.remove('has-weight-selected');
        const weightLabel = row.querySelector('[data-details-weight-label]');
        if (weightLabel instanceof HTMLElement) weightLabel.textContent = 'Coming Soon';
      }
    });
  }

  /**
   * @param {HTMLElement} row
   * @param {HTMLInputElement | null} input
   * @returns {number | null}
   */
  #getRowSelectableMax(row, input) {
    const fromApi = /** @type {any} */ (window).plpVariantDetails?.getSelectableWeightEntriesForSku;
    const sku = String(row.dataset.variantSku || '').trim();
    const inventoryRaw = row.dataset.variantInventoryQty || (input ? input.getAttribute('max') : '') || '';
    const inventoryMax = inventoryRaw !== '' ? Number(inventoryRaw) : NaN;
    if (!sku || typeof fromApi !== 'function') {
      return Number.isFinite(inventoryMax) && inventoryMax >= 0 ? inventoryMax : null;
    }
    const detailsRoot = row.closest('[data-plp-expanded-details], [data-quick-view-details]');
    /** @type {Set<string>} */
    const exclude = new Set();
    if (detailsRoot instanceof HTMLElement) {
      detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((other) => {
        if (!(other instanceof HTMLElement) || other === row) return;
        String(other.dataset.serialNumbersValue || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((serial) => exclude.add(serial));
      });
    }
    const optionMax = fromApi(sku, exclude).length;
    if (Number.isFinite(inventoryMax) && inventoryMax >= 0) return Math.min(inventoryMax, optionMax);
    return optionMax;
  }

  /** @param {HTMLElement} row @param {number} requestedQty */
  #setModalOnlyQty(row, requestedQty) {
    const cartBaseline = Number(row.dataset.cartBaselineQty || 0);
    const cartBaselineNum =
      Number.isFinite(cartBaseline) && cartBaseline > 0 ? Math.floor(cartBaseline) : 0;
    if (row.dataset.variantAvailable === 'false' && cartBaselineNum <= 0) return;
    if (row.dataset.qvmInventoryPending === 'true') return;
    const input = row.querySelector('[data-details-qty-input][data-details-qty-modal-only]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    const reconcile = /** @type {any} */ (window).plpVariantDetails?.reconcileRowQuantitySelection;
    let next = Number.isFinite(requestedQty) ? Math.max(0, Math.floor(requestedQty)) : 0;

    if (typeof reconcile === 'function') {
      next = reconcile(row, next, { silent: true });
    } else {
      const max = this.#getRowSelectableMax(row, input);
      if (max != null) next = Math.min(next, Math.max(0, max));
      input.value = String(next);
      if (next <= 0) {
        delete row.dataset.weightValue;
        delete row.dataset.serialNumbersValue;
        row.classList.remove('has-weight-selected');
      }
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const displayInput = row.querySelector('[data-qvm-qty-display-input]');
    if (displayInput instanceof HTMLInputElement) displayInput.value = String(next);
    const detailsRoot = this.#root?.querySelector(SEL.detailsRoot);
    if (detailsRoot instanceof HTMLElement) this.#initQtyRowStates(detailsRoot);
    this.#updateCtaDisabled();
    this.#syncSelectionUi();
  }

  /** @param {HTMLInputElement} displayInput @param {boolean} finalize */
  #handleModalOnlyQtyDisplayInput(displayInput, finalize) {
    const row = displayInput.closest('[data-details-variant-row]');
    if (!(row instanceof HTMLElement)) return;
    const digitsOnly = String(displayInput.value || '').replace(/[^\d]/g, '');
    if (digitsOnly !== displayInput.value) displayInput.value = digitsOnly;
    if (!finalize && digitsOnly === '') return;
    const next = digitsOnly === '' ? 0 : Number.parseInt(digitsOnly, 10);
    this.#setModalOnlyQty(row, Number.isFinite(next) ? next : 0);
  }

  /**
   * @param {HTMLElement} row
   * @param {number} qty
   * @returns {boolean}
   */
  #isRowSelectionValid(row, qty) {
    if (!(row instanceof HTMLElement) || row.dataset.qvmInventoryPending === 'true') return false;
    if (!Number.isFinite(qty) || qty <= 0) return false;
    const cartBaseline = Number(row.dataset.cartBaselineQty || 0);
    if (row.dataset.variantAvailable === 'false' && cartBaseline <= 0) return false;
    const sku = String(row.dataset.variantSku || '').trim();
    if (!sku) return true;

    const weight = String(row.dataset.weightValue || '').trim();
    const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
    if (!weight || !serialNumbers) return false;
    const weightParts = weight
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const serialParts = serialNumbers
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return weightParts.length === qty && serialParts.length === qty;
  }

  /**
   * True when draft differs from cart and is ready to commit.
   * @param {HTMLElement} row
   * @param {number} qty
   */
  #isRowCommittable(row, qty) {
    if (!(row instanceof HTMLElement) || row.dataset.qvmInventoryPending === 'true') return false;
    const plp = /** @type {any} */ (window).plpVariantDetails;
    if (plp && typeof plp.isRowDraftCommittable === 'function') {
      return plp.isRowDraftCommittable(row);
    }
    const cartBaseline = Number(row.dataset.cartBaselineQty || 0);
    const baselineNum = Number.isFinite(cartBaseline) && cartBaseline > 0 ? Math.floor(cartBaseline) : 0;
    if (qty <= 0) return baselineNum > 0 && row.dataset.selectionDirty === 'true';
    if (!this.#isRowSelectionValid(row, qty)) return false;
    if (qty !== baselineNum) return true;
    const serials = String(row.dataset.serialNumbersValue || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .join(',');
    const baselineSerials = String(row.dataset.cartBaselineSerials || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .join(',');
    return serials !== baselineSerials;
  }

  /** Reflect qty / weight selection in table row UI + footer summary (presentation only). */
  #syncSelectionUi() {
    const detailsRoot = this.#root?.querySelector(SEL.detailsRoot);
    if (!(detailsRoot instanceof HTMLElement)) return;

    let selectedVariants = 0;
    let totalQty = 0;
    let totalWeight = 0;

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const input = row.querySelector('[data-details-qty-input]');
      const qty = input instanceof HTMLInputElement ? readRowQty(input) : 0;
      const displayInput = row.querySelector('[data-qvm-qty-display-input]');
      if (displayInput instanceof HTMLInputElement) {
        displayInput.value = String(qty);
      }

      const isValidSelection = this.#isRowSelectionValid(row, qty);
      const hasSelection = qty > 0 || row.classList.contains('has-weight-selected');
      row.classList.toggle('is-selected', hasSelection);

      const cartBaseline = Number(row.dataset.cartBaselineQty || 0);
      if (row.dataset.variantAvailable === 'false' && cartBaseline <= 0) return;
      // Footer derives from full draft selection (including cart-hydrated units).
      if (qty > 0 && isValidSelection) {
        selectedVariants += 1;
        totalQty += qty;
        const weightParts = String(row.dataset.weightValue || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        totalWeight += weightParts.reduce((sum, part) => sum + parseWeightGrams(part), 0);
      }
    });

    const countEl = this.#root?.querySelector('[data-qvm-selected-count]');
    const countValueEl = this.#root?.querySelector('[data-qvm-selected-count-value]');
    const qtyEl = this.#root?.querySelector('[data-qvm-total-qty]');
    const weightEl = this.#root?.querySelector('[data-qvm-total-weight]');
    if (countEl) countEl.textContent = String(selectedVariants);
    if (countValueEl) countValueEl.textContent = String(selectedVariants);
    if (qtyEl) qtyEl.textContent = String(totalQty);
    if (weightEl) {
      weightEl.textContent = `${totalWeight.toFixed(2)}g`;
    }
  }

  /** @param {MouseEvent} e */
  #onRootClick(e) {
    const rawTarget = e.target;
    const t =
      rawTarget instanceof Element
        ? rawTarget
        : rawTarget instanceof Node
          ? rawTarget.parentElement
          : null;
    if (!(t instanceof Element)) return;

    const infoTip = t.closest('[data-matrix-info-tip]');
    if (infoTip instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
      this.#toggleMatrixInfoTooltip(infoTip);
      return;
    }
    if (!(t.closest('.quick-view-chain-matrix__tooltip') || t.closest('.bulk-order-matrix__tooltip'))) {
      this.#hideMatrixInfoTooltip();
    }

    if (e.target === this.#root) {
      this.close();
      return;
    }
    if (t.closest('[data-quick-modal-close]')) {
      e.preventDefault();
      this.close();
      return;
    }
    if (t.classList.contains('quick-modal__overlay')) {
      this.close();
      return;
    }

    const thumb = t.closest('[data-qvm-thumb]');
    if (thumb instanceof HTMLElement) {
      e.preventDefault();
      this.#switchImage(thumb);
      return;
    }

    const viewImages = t.closest('[data-qvm-view-images]');
    if (viewImages instanceof HTMLButtonElement) {
      e.preventDefault();
      const gallery = viewImages.closest('[data-qvm-gallery]');
      if (!(gallery instanceof HTMLElement)) return;
      let images = Array.from(gallery.querySelectorAll('[data-qvm-popup-image]'))
        .map((item) => ({
          src: item.getAttribute('data-image-src') || '',
          alt: item.getAttribute('data-image-alt') || '',
        }))
        .filter((item) => item.src);
      const mainImg = gallery.querySelector('[data-qvm-main-image]');
      const mainSrc =
        (mainImg instanceof HTMLImageElement && (mainImg.getAttribute('src') || mainImg.currentSrc)) ||
        gallery.getAttribute('data-gallery-src') ||
        '';
      // Single-image products still open the viewer from the main stage image.
      if (!images.length && mainSrc) {
        images = [
          {
            src: mainSrc,
            alt: mainImg instanceof HTMLImageElement ? mainImg.getAttribute('alt') || '' : '',
          },
        ];
      }
      if (!images.length) return;
      const activeIndex = Math.max(
        0,
        images.findIndex((item) => item.src === mainSrc)
      );
      /** @type {any} */ (window).plpVariantDetails?.openImageViewer?.(images, activeIndex, viewImages);
      return;
    }

    const qvmMinus = t.closest('[data-qvm-qty-minus]');
    const qvmPlus = t.closest('[data-qvm-qty-plus]');
    if (qvmMinus || qvmPlus) {
      e.preventDefault();
      e.stopPropagation();
      const trigger = qvmMinus || qvmPlus;
      const row = trigger instanceof Element ? trigger.closest('[data-details-variant-row]') : null;
      if (!(row instanceof HTMLElement)) return;
      const cartBaseline = Number(row.dataset.cartBaselineQty || 0);
      if (
        row.dataset.variantAvailable === 'false' &&
        !(Number.isFinite(cartBaseline) && cartBaseline > 0)
      ) {
        return;
      }
      if (row.dataset.qvmInventoryPending === 'true') return;
      const input = row.querySelector('[data-details-qty-input][data-details-qty-modal-only]');
      if (!(input instanceof HTMLInputElement) || input.disabled) return;
      const current = readRowQty(input);
      const next = qvmPlus ? current + 1 : Math.max(0, current - 1);
      this.#setModalOnlyQty(row, next);
      return;
    }

    const addBtn = t.closest('[data-add-selected]');
    if (addBtn instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
      this.#submitCart();
      return;
    }

    const wmsRetry = t.closest('[data-qvm-wms-retry]');
    if (wmsRetry instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
      this.#retryWmsDetails();
    }
  }

  /** @param {HTMLElement} thumbBtn */
  #switchImage(thumbBtn) {
    const gallery = thumbBtn.closest('[data-qvm-gallery]');
    if (!(gallery instanceof HTMLElement)) return;
    const src = thumbBtn.getAttribute('data-qvm-thumb-src');
    const main = gallery.querySelector('[data-qvm-main-image]');
    if (!src || !(main instanceof HTMLImageElement)) return;

    const placeholderImage =
      gallery.getAttribute('data-placeholder-product-image') ||
      getPlaceholderProductImage(this.#root);
    if (placeholderImage) {
      main.onerror = () => {
        main.onerror = null;
        main.removeAttribute('srcset');
        main.src = placeholderImage;
      };
    }
    main.src = src;
    main.style.opacity = '1';
    gallery.setAttribute('data-gallery-src', src);

    const thumbs = gallery.querySelectorAll('[data-qvm-thumb]');
    let activeIndex = 0;
    thumbs.forEach((el, index) => {
      if (!(el instanceof HTMLElement)) return;
      const active = el === thumbBtn;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (active) activeIndex = index;
    });

    const popupImages = gallery.querySelectorAll('[data-qvm-popup-image]');
    popupImages.forEach((node, index) => {
      if (!(node instanceof HTMLElement)) return;
      if (index === activeIndex) node.setAttribute('data-active-image', '');
      else node.removeAttribute('data-active-image');
    });

    // Horizontal chain gallery: open the existing lightbox at the selected image.
    if (gallery.classList.contains('quick-view-image--horizontal')) {
      const images = Array.from(popupImages)
        .map((item) => ({
          src: item.getAttribute('data-image-src') || '',
          alt: item.getAttribute('data-image-alt') || '',
        }))
        .filter((item) => item.src);
      if (!images.length && src) {
        images.push({
          src,
          alt: main.getAttribute('alt') || '',
        });
      }
      if (!images.length) return;
      const viewBtn = gallery.querySelector('[data-qvm-view-images]');
      /** @type {any} */ (window).plpVariantDetails?.openImageViewer?.(
        images,
        activeIndex,
        viewBtn instanceof HTMLElement ? viewBtn : thumbBtn
      );
    }
  }

  #updateCtaDisabled() {
    const btn = this.#root?.querySelector('[data-add-selected]');
    const detailsRoot = this.#root?.querySelector(SEL.detailsRoot);
    if (!(btn instanceof HTMLButtonElement) || !(detailsRoot instanceof HTMLElement)) return;

    let committableRows = 0;
    let invalidChangedRows = 0;
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const cartBaseline = Number(row.dataset.cartBaselineQty || 0);
      if (row.dataset.variantAvailable === 'false' && cartBaseline <= 0) return;
      const input = row.querySelector('[data-details-qty-input]');
      const qty = input instanceof HTMLInputElement ? readRowQty(input) : 0;
      const plp = /** @type {any} */ (window).plpVariantDetails;
      const differs =
        plp && typeof plp.isRowDraftDifferentFromCart === 'function'
          ? plp.isRowDraftDifferentFromCart(row)
          : qty !== (Number.isFinite(cartBaseline) ? cartBaseline : 0);
      if (!differs) return;
      if (this.#isRowCommittable(row, qty)) committableRows += 1;
      else if (qty > 0) invalidChangedRows += 1;
    });
    btn.disabled = this.#isSubmittingCart || committableRows <= 0 || invalidChangedRows > 0;
  }

  async #submitCart() {
    const product = this.#product;
    const btn = this.#root?.querySelector('[data-add-selected]');
    const detailsRoot = this.#root?.querySelector(SEL.detailsRoot);
    if (!product || !(btn instanceof HTMLButtonElement) || !(detailsRoot instanceof HTMLElement)) return;
    if (this.#isSubmittingCart) return;

    const goldPricing = /** @type {typeof window.AoneGoldPricing | undefined} */ (window.AoneGoldPricing);
    if (goldPricing) {
      await goldPricing.waitForReady();
    }

    const plp = /** @type {any} */ (window).plpVariantDetails;
    if (!plp || typeof plp.commitDraftRowsToCart !== 'function') {
      console.warn('[quick-view-modal] Draft cart commit helper unavailable.');
      return;
    }

    let hasCommittable = false;
    let weightValidationError = '';
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || weightValidationError) return;
      if (row.dataset.qvmInventoryPending === 'true') return;
      const input = row.querySelector('[data-details-qty-input]');
      const qty = input instanceof HTMLInputElement ? readRowQty(input) : 0;
      const differs =
        typeof plp.isRowDraftDifferentFromCart === 'function'
          ? plp.isRowDraftDifferentFromCart(row)
          : true;
      if (!differs) return;
      if (this.#isRowCommittable(row, qty)) {
        hasCommittable = true;
        return;
      }
      if (qty > 0) {
        const sku = String(row.dataset.variantSku || '').trim();
        if (!sku) return;
        const weight = String(row.dataset.weightValue || '').trim();
        const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
        if (!weight || !serialNumbers) {
          weightValidationError = 'Select weight for all variants with quantity greater than 0.';
        } else {
          weightValidationError = 'Weight selection must match quantity for each variant.';
        }
      }
    });

    if (weightValidationError) {
      console.warn('[quick-view-modal] ' + weightValidationError);
      return;
    }
    if (!hasCommittable) {
      console.warn('[quick-view-modal] No valid quantity selected for cart add.');
      return;
    }

    const labelEl = btn.querySelector('[data-add-selected-label]');
    const defaultLabel = labelEl ? String(labelEl.textContent || 'Add Selected To Cart') : '';
    this.#isSubmittingCart = true;
    btn.disabled = true;
    btn.classList.add('quick-view-add-to-cart__button--loading');
    if (labelEl) labelEl.textContent = 'Adding...';

    try {
      const { cart: mergedCart } = await plp.commitDraftRowsToCart(detailsRoot);

      if (window.aoneCartCount) {
        const cart =
          mergedCart ||
          (await window.aoneCartCount.fetchAndPublish('/cart.js', 'quick-view-modal'));
        const inventoryApiAfter =
          /** @type {any} */ (window).AOneWmsAvailability || /** @type {any} */ (window).WMSInventory;
        if (inventoryApiAfter?.handleSuccessfulCartMutation) {
          // Badge only — inventory event comes from handleSuccessfulCartMutation.
          // Publishing cart:update here caused a second full-grid refresh.
          if (cart && typeof cart.item_count === 'number') {
            window.aoneCartCount.syncBadgeDom?.(cart.item_count);
          }
          await inventoryApiAfter.handleSuccessfulCartMutation(cart, { reason: 'cart-add' });
        } else if (mergedCart && window.aoneCartCount.publish) {
          window.aoneCartCount.publish(mergedCart, 'quick-view-modal');
        }
      } else {
        const inventoryApiAfter =
          /** @type {any} */ (window).AOneWmsAvailability || /** @type {any} */ (window).WMSInventory;
        if (inventoryApiAfter?.handleSuccessfulCartMutation) {
          await inventoryApiAfter.handleSuccessfulCartMutation(mergedCart, { reason: 'cart-add' });
        }
      }

      // Reconcile open modal + product cards before close so availability drops immediately.
      if (this.#product && this.#root && !this.#root.hasAttribute('hidden')) {
        this.#logInventoryDebug(this.#product, 'after-cart-add');
        this.#hydrate(this.#product, { matrixOnly: true });
      }
      this.#isSubmittingCart = false;
      this.close();
      return;
    } catch (e) {
      console.error(e);
      this.#isSubmittingCart = false;
      if (labelEl) labelEl.textContent = defaultLabel;
      this.#updateCtaDisabled();
    } finally {
      btn.classList.remove('quick-view-add-to-cart__button--loading');
    }
  }

  /** @param {KeyboardEvent} e */
  #handleKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (this.#matrixInfoTooltip && !this.#matrixInfoTooltip.hidden) {
        this.#hideMatrixInfoTooltip();
        this.#matrixInfoActiveBtn?.focus();
        return;
      }
      this.close();
    }
  }

  /** Bind once: hover/focus/touch tooltips for matrix info icons (event delegation). */
  #bindMatrixInfoTooltips() {
    if (!this.#root || this.#root.dataset.matrixInfoTipsBound === 'true') return;
    this.#root.dataset.matrixInfoTipsBound = 'true';

    this.#root.addEventListener(
      'pointerover',
      (e) => {
        const btn = e.target instanceof Element ? e.target.closest('[data-matrix-info-tip]') : null;
        if (!(btn instanceof HTMLButtonElement) || !this.#root?.contains(btn)) return;
        const related = e.relatedTarget;
        if (related instanceof Node && btn.contains(related)) return;
        this.#cancelMatrixInfoHide();
        this.#showMatrixInfoTooltip(btn);
      },
      true
    );

    this.#root.addEventListener(
      'pointerout',
      (e) => {
        const btn = e.target instanceof Element ? e.target.closest('[data-matrix-info-tip]') : null;
        if (!(btn instanceof HTMLButtonElement)) return;
        const related = e.relatedTarget;
        if (
          related instanceof Node &&
          (btn.contains(related) || (this.#matrixInfoTooltip && this.#matrixInfoTooltip.contains(related)))
        ) {
          return;
        }
        if (document.activeElement === btn) return;
        this.#scheduleMatrixInfoHide();
      },
      true
    );

    this.#root.addEventListener(
      'focusin',
      (e) => {
        const btn = e.target instanceof Element ? e.target.closest('[data-matrix-info-tip]') : null;
        if (btn instanceof HTMLButtonElement) {
          this.#cancelMatrixInfoHide();
          this.#showMatrixInfoTooltip(btn);
        }
      },
      true
    );

    this.#root.addEventListener(
      'focusout',
      (e) => {
        const btn = e.target instanceof Element ? e.target.closest('[data-matrix-info-tip]') : null;
        if (!(btn instanceof HTMLButtonElement)) return;
        const related = e.relatedTarget;
        if (
          related instanceof Node &&
          (btn.contains(related) || (this.#matrixInfoTooltip && this.#matrixInfoTooltip.contains(related)))
        ) {
          return;
        }
        this.#scheduleMatrixInfoHide();
      },
      true
    );

    this.#root.addEventListener(
      'scroll',
      () => {
        this.#hideMatrixInfoTooltip();
      },
      true
    );
  }

  #cancelMatrixInfoHide() {
    if (this.#matrixInfoHideTimer != null) {
      clearTimeout(this.#matrixInfoHideTimer);
      this.#matrixInfoHideTimer = null;
    }
  }

  #scheduleMatrixInfoHide() {
    this.#cancelMatrixInfoHide();
    this.#matrixInfoHideTimer = setTimeout(() => {
      this.#matrixInfoHideTimer = null;
      const tip = this.#matrixInfoTooltip;
      const active = document.activeElement;
      if (tip instanceof HTMLElement && active instanceof Node && tip.contains(active)) return;
      this.#hideMatrixInfoTooltip();
    }, 180);
  }

  /** @returns {HTMLElement} */
  #ensureMatrixInfoTooltip() {
    if (this.#matrixInfoTooltip instanceof HTMLElement) return this.#matrixInfoTooltip;
    const tip = document.createElement('div');
    tip.className = 'quick-view-chain-matrix__tooltip bulk-order-matrix__tooltip';
    tip.id = 'QuickViewChainMatrixTooltip';
    tip.setAttribute('role', 'dialog');
    tip.setAttribute('aria-label', 'Variant SKU');
    tip.hidden = true;
    tip.addEventListener('pointerenter', () => this.#cancelMatrixInfoHide());
    tip.addEventListener('pointerleave', (e) => {
      const related = e.relatedTarget;
      if (
        related instanceof Node &&
        (this.#matrixInfoActiveBtn?.contains(related) || tip.contains(related))
      ) {
        return;
      }
      this.#scheduleMatrixInfoHide();
    });
    tip.addEventListener('focusin', () => this.#cancelMatrixInfoHide());
    tip.addEventListener('focusout', (e) => {
      const related = e.relatedTarget;
      if (
        related instanceof Node &&
        (this.#matrixInfoActiveBtn?.contains(related) || tip.contains(related))
      ) {
        return;
      }
      this.#scheduleMatrixInfoHide();
    });
    tip.addEventListener('click', (e) => {
      const copyBtn = e.target instanceof Element ? e.target.closest('[data-matrix-sku-copy]') : null;
      if (!(copyBtn instanceof HTMLButtonElement) || !tip.contains(copyBtn)) return;
      e.preventDefault();
      e.stopPropagation();
      this.#copyMatrixSku(copyBtn);
    });
    document.body.appendChild(tip);
    this.#matrixInfoTooltip = tip;
    return tip;
  }

  /**
   * @param {HTMLButtonElement} btn
   * @returns {string}
   */
  #buildMatrixInfoTooltipHtml(btn) {
    const sku = String(btn.getAttribute('data-tip-sku') || '').trim() || '—';
    const canCopy = sku !== '—';

    return `
      <div class="quick-view-chain-matrix__tooltip-row quick-view-chain-matrix__tooltip-row--sku">
        <span class="quick-view-chain-matrix__tooltip-sku-text">SKU: ${escapeAttr(sku)}</span>
        ${
          canCopy
            ? `<button type="button" class="quick-view-chain-matrix__tooltip-copy" data-matrix-sku-copy data-sku="${escapeAttr(sku)}" aria-label="Copy SKU ${escapeAttr(sku)}">Copy</button>`
            : ''
        }
      </div>
    `;
  }

  /**
   * @param {HTMLButtonElement} copyBtn
   */
  async #copyMatrixSku(copyBtn) {
    const sku = String(copyBtn.getAttribute('data-sku') || '').trim();
    if (!sku) return;
    this.#cancelMatrixInfoHide();
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(sku);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) {
      const range = document.createRange();
      const textEl = this.#matrixInfoTooltip?.querySelector('.quick-view-chain-matrix__tooltip-sku-text');
      if (textEl) {
        range.selectNodeContents(textEl);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      return;
    }
    copyBtn.textContent = 'Copied';
    copyBtn.classList.add('is-copied');
    if (this.#matrixInfoCopyResetTimer != null) clearTimeout(this.#matrixInfoCopyResetTimer);
    this.#matrixInfoCopyResetTimer = setTimeout(() => {
      this.#matrixInfoCopyResetTimer = null;
      if (!copyBtn.isConnected) return;
      copyBtn.textContent = 'Copy';
      copyBtn.classList.remove('is-copied');
    }, 1600);
  }

  /** @param {HTMLButtonElement} btn */
  #positionMatrixInfoTooltip(btn) {
    const tip = this.#ensureMatrixInfoTooltip();
    if (tip.hidden) return;
    const rect = btn.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const margin = 8;
    // Prefer above the info icon so the popover does not cover qty / status text.
    let top = rect.top - tipRect.height - margin;
    let left = rect.right - tipRect.width;

    if (left < margin) left = margin;
    if (left + tipRect.width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - tipRect.width - margin);
    }
    if (top < margin) {
      top = rect.bottom + margin;
    }
    if (top + tipRect.height > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - tipRect.height - margin);
    }

    tip.style.top = `${Math.round(top)}px`;
    tip.style.left = `${Math.round(left)}px`;
  }

  /** @param {HTMLButtonElement} btn */
  #showMatrixInfoTooltip(btn) {
    this.#cancelMatrixInfoHide();
    if (this.#matrixInfoActiveBtn && this.#matrixInfoActiveBtn !== btn) {
      this.#matrixInfoActiveBtn.setAttribute('aria-expanded', 'false');
      this.#matrixInfoActiveBtn.removeAttribute('aria-describedby');
    }
    const tip = this.#ensureMatrixInfoTooltip();
    tip.innerHTML = this.#buildMatrixInfoTooltipHtml(btn);
    tip.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    btn.setAttribute('aria-describedby', tip.id);
    this.#matrixInfoActiveBtn = btn;
    this.#matrixInfoShownAt = Date.now();
    requestAnimationFrame(() => this.#positionMatrixInfoTooltip(btn));
  }

  #hideMatrixInfoTooltip() {
    this.#cancelMatrixInfoHide();
    if (this.#matrixInfoCopyResetTimer != null) {
      clearTimeout(this.#matrixInfoCopyResetTimer);
      this.#matrixInfoCopyResetTimer = null;
    }
    if (this.#matrixInfoActiveBtn) {
      this.#matrixInfoActiveBtn.setAttribute('aria-expanded', 'false');
      this.#matrixInfoActiveBtn.removeAttribute('aria-describedby');
      this.#matrixInfoActiveBtn = null;
    }
    if (this.#matrixInfoTooltip) {
      this.#matrixInfoTooltip.hidden = true;
      this.#matrixInfoTooltip.innerHTML = '';
    }
  }

  /** @param {HTMLButtonElement} btn */
  #toggleMatrixInfoTooltip(btn) {
    const alreadyOpen =
      this.#matrixInfoActiveBtn === btn &&
      this.#matrixInfoTooltip instanceof HTMLElement &&
      !this.#matrixInfoTooltip.hidden;
    // Ignore the click that immediately follows pointerover/focus show (desktop).
    if (alreadyOpen && Date.now() - this.#matrixInfoShownAt > 250) {
      this.#hideMatrixInfoTooltip();
      return;
    }
    this.#showMatrixInfoTooltip(btn);
  }

  /**
   * Dev-only inventory trace. Guarded by AOneWmsConfig.debug.
   * @param {object} product
   * @param {string} reason
   */
  #logInventoryDebug(product, reason) {
    const cfg = /** @type {any} */ (window).AOneWmsConfig;
    if (!cfg?.debug) return;
    const inventoryApi =
      /** @type {any} */ (window).AOneWmsAvailability || /** @type {any} */ (window).WMSInventory;
    if (!inventoryApi) return;
    const productId = normalizeShopifyId(product?.id);
    const rows = [];
    (Array.isArray(product?.variants) ? product.variants : []).forEach((variant) => {
      const sku = normalizeSku(variant?.sku != null ? String(variant.sku) : '');
      if (!sku) return;
      const calc =
        typeof inventoryApi.calculateEffectiveAvailability === 'function'
          ? inventoryApi.calculateEffectiveAvailability({
              productId,
              variantId: variant.id,
              sku,
            })
          : inventoryApi.getSkuAvailability?.(productId, sku);
      rows.push({
        variantId: variant.id,
        sku,
        rawAvailableQty: calc?.rawAvailableQty ?? calc?.rawAvailableCount ?? null,
        reservedInCartQty: calc?.reservedInCartQty ?? null,
        effectiveAvailableQty: calc?.effectiveAvailableQty ?? calc?.count ?? null,
        minAvailableWeight: calc?.minAvailableWeight ?? calc?.minWeight ?? null,
        storefrontStatus: calc?.storefrontStatus || '',
      });
    });
    console.debug('[AONE QuickView Inventory]', {
      reason,
      productId,
      handle: product?.handle || this.#activeProductHandle,
      requestId: this.#activeRequestId,
      cartReady: typeof inventoryApi.isCartStateReady === 'function' ? inventoryApi.isCartStateReady() : null,
      rows,
    });
  }

  #bindCartInventorySubscription() {
    this.#unbindCartInventorySubscription();
    const inventoryApi =
      /** @type {any} */ (window).AOneWmsAvailability || /** @type {any} */ (window).WMSInventory;
    const sessionId = this.#activeRequestId;
    const onUpdate = (detail) => {
      if (sessionId !== this.#activeRequestId) return;
      if (!this.#product || !this.#root || this.#root.hasAttribute('hidden')) return;
      const cartReady =
        !inventoryApi ||
        typeof inventoryApi.isCartStateReady !== 'function' ||
        inventoryApi.isCartStateReady() === true;
      const loading =
        (detail && (detail.loading === true || detail.cartReady === false)) || !cartReady;
      if (loading) {
        this.#hydrate(this.#product, { matrixOnly: true, wmsLoading: true });
        return;
      }
      this.#logInventoryDebug(this.#product, detail?.reason || 'cart-inventory-updated');
      this.#hydrate(this.#product, { matrixOnly: true });
    };
    if (inventoryApi && typeof inventoryApi.subscribeToCartChanges === 'function') {
      this.#cartInventoryUnsub = inventoryApi.subscribeToCartChanges(onUpdate);
      return;
    }
    const eventName =
      (inventoryApi && inventoryApi.CART_INVENTORY_EVENT) || 'aone:cart-inventory-updated';
    this.#cartInventoryListener = (event) => onUpdate(event && event.detail);
    document.addEventListener(eventName, this.#cartInventoryListener);
  }

  #unbindCartInventorySubscription() {
    if (typeof this.#cartInventoryUnsub === 'function') {
      this.#cartInventoryUnsub();
      this.#cartInventoryUnsub = null;
    }
    if (this.#cartInventoryListener) {
      const inventoryApi =
        /** @type {any} */ (window).AOneWmsAvailability || /** @type {any} */ (window).WMSInventory;
      const eventName =
        (inventoryApi && inventoryApi.CART_INVENTORY_EVENT) || 'aone:cart-inventory-updated';
      document.removeEventListener(eventName, this.#cartInventoryListener);
      this.#cartInventoryListener = null;
    }
  }

  /**
   * Subscribe to shared WMS product availability so an already-open Quick View
   * clears its matrix skeleton when products-batch finishes for this product
   * or any custom.linked_products sibling.
   * Only updates while inventory is still pending — avoids resetting user selections.
   * @param {string | string[]} productIds
   */
  #bindProductAvailabilitySubscription(productIds) {
    this.#unbindProductAvailabilitySubscription();
    const ids = (Array.isArray(productIds) ? productIds : [productIds])
      .map((value) => normalizeShopifyId(value))
      .filter(Boolean);
    if (!ids.length) return;

    const inventoryApi =
      /** @type {any} */ (window).AOneWmsAvailability || /** @type {any} */ (window).WMSInventory;
    if (!inventoryApi || typeof inventoryApi.subscribeToProductAvailability !== 'function') return;

    const sessionId = this.#activeRequestId;
    /** @type {Array<() => void>} */
    const unsubs = [];
    ids.forEach((id) => {
      const unsub = inventoryApi.subscribeToProductAvailability(id, (snapshot) => {
      if (sessionId !== this.#activeRequestId) return;
      if (!this.#product || !this.#root || this.#root.hasAttribute('hidden')) return;
      const family = new Set(familyProductIds(this.#product));
      if (family.size && !family.has(id)) return;

      const detailsRoot = this.#root.querySelector(SEL.detailsRoot);
      const inventoryStillPending =
        (detailsRoot instanceof HTMLElement &&
          (detailsRoot.dataset.isInventoryLoading === 'true' ||
            detailsRoot.dataset.wmsDetailsLoading === 'true')) ||
        !!this.#root.querySelector('[data-qvm-inventory-pending="true"]');

      // After the matrix has real data, cart subscription owns subsequent repaints.
      if (!inventoryStillPending) return;

      const status = String(snapshot?.status || '');
      const cartReady =
        typeof inventoryApi.isCartStateReady !== 'function' || inventoryApi.isCartStateReady() === true;
      const stillLoading =
        snapshot?.loading === true ||
        status === 'loading' ||
        status === 'idle' ||
        !cartReady;

      if (stillLoading) {
        this.#hydrate(this.#product, { matrixOnly: true, wmsLoading: true });
        return;
      }

      if (status === 'error') {
        this.#hydrate(this.#product, { matrixOnly: true, wmsError: true });
        return;
      }

      // Loaded — including zero availability / Coming Soon (completed response, not infinite load).
      syncFamilyWeightCaches(this.#product);
      applyBatchDataToProduct(this.#product);
      this.#logInventoryDebug(this.#product, 'product-availability-updated');
      this.#hydrate(this.#product, { matrixOnly: true });
      });
      if (typeof unsub === 'function') unsubs.push(unsub);
    });
    this.#productAvailabilityUnsub = () => {
      unsubs.forEach((unsub) => unsub());
    };
  }

  #unbindProductAvailabilitySubscription() {
    if (typeof this.#productAvailabilityUnsub === 'function') {
      this.#productAvailabilityUnsub();
    }
    this.#productAvailabilityUnsub = null;
  }

  close() {
    if (!this.#root) return;
    this.#hideMatrixInfoTooltip();
    this.#unbindCartInventorySubscription();
    this.#unbindProductAvailabilitySubscription();
    this.#isSubmittingCart = false;
    this.#activeRequestId += 1;
    this.#activeProductHandle = '';
    this.#fetchAbort?.abort();
    this.#fetchAbort = null;
    this.#root.classList.remove('quick-modal--open');
    this.#root.classList.remove('quick-modal--loading');
    document.body.style.overflow = this.#prevBodyOverflow;
    document.documentElement.style.overflow = this.#prevHtmlOverflow;
    document.body.classList.remove('quick-modal-open');
    document.documentElement.classList.remove('quick-modal-open');
    document.removeEventListener('keydown', this.#onKey);
    this.#resetQuickViewState();
    setTimeout(() => {
      if (this.#root && !this.#root.classList.contains('quick-modal--open')) {
        this.#root.setAttribute('hidden', '');
        this.#root.setAttribute('aria-hidden', 'true');
      }
    }, 200);
    this.#restoreImageSearchDialog();
  }
}

const qvGlobal = /** @type {any} */ (window);
if (!qvGlobal.__aoneQuickViewModalController) {
  qvGlobal.__aoneQuickViewModalController = new QuickViewModalController();
}
